import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  ButtonBase,
  InputBase,
  Slider,
  Avatar,
  Collapse,
} from '@mui/material';
import {
  Close as CloseIcon,
  Place as PlaceIcon,
  Apartment as ApartmentIcon,
  Terrain as TerrainIcon,
  TrendingUp as TrendingUpIcon,
  Receipt as ReceiptIcon,
  FormatListBulleted as ListIcon,
  Groups as GroupsIcon,
  Insights as InsightsIcon,
  EmojiEvents as EmojiEventsIcon,
  GridView as GridViewIcon,
  Layers as LayersIcon,
  Whatshot as WhatshotIcon,
  CenterFocusStrong as CenterFocusStrongIcon,
  Map as MapIcon,
  AutoFixHigh as AutoFixHighIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Tune as TuneIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import {
  MapContainer,
  TileLayer,
  Marker,
  Polygon,
  Polyline,
  Popup,
  CircleMarker,
  Circle,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { DISTRICTS, MapProject, DistrictInfo, Region, ProjectType } from './mockData';
import { api, ProjectDetails, fetchOnemapLandLotsBulk, OnemapBulkLandLotResultItem } from '../../services/api';
import { KmlPolygon, loadKml } from './kmlLoader';
import districtKmlUrl from './district.kml';

type ViewMode = 'project' | 'cop';
type CopMode = 'project' | 'district';
type PlotMode = 'pin' | 'plot' | 'label';
type HeatMetric = 'transactions' | 'listings' | 'eoi';
type BasemapId = 'voyager' | 'light' | 'dark' | 'streets' | 'satellite';

interface BasemapSpec {
  id: BasemapId;
  label: string;
  url: string;
  attribution: string;
  subdomains?: string[];
  background: string; // canvas color while tiles load
  tone: 'light' | 'dark';
}

const BASEMAPS: Record<BasemapId, BasemapSpec> = {
  voyager: {
    id: 'voyager',
    label: 'Voyager',
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
    attribution: '&copy; OpenStreetMap &copy; CARTO',
    subdomains: ['a', 'b', 'c', 'd'],
    background: '#eef3f7',
    tone: 'light',
  },
  light: {
    id: 'light',
    label: 'Light',
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; OpenStreetMap &copy; CARTO',
    subdomains: ['a', 'b', 'c', 'd'],
    background: '#f5f5f5',
    tone: 'light',
  },
  dark: {
    id: 'dark',
    label: 'Dark',
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; OpenStreetMap &copy; CARTO',
    subdomains: ['a', 'b', 'c', 'd'],
    background: '#0b1220',
    tone: 'dark',
  },
  streets: {
    id: 'streets',
    label: 'Streets',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; OpenStreetMap contributors',
    subdomains: ['a', 'b', 'c'],
    background: '#e9e7d8',
    tone: 'light',
  },
  satellite: {
    id: 'satellite',
    label: 'Satellite',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri',
    background: '#0b1220',
    tone: 'dark',
  },
};

// Tight Singapore-only bounds so the map fits the island, not surrounding Malaysia/Indonesia.
const SG_BOUNDS: L.LatLngBoundsExpression = [
  [1.220, 103.610],
  [1.472, 104.040],
];
// Soft pan limit (a little roomier than the fit bounds).
const SG_MAX_BOUNDS: L.LatLngBoundsExpression = [
  [1.150, 103.500],
  [1.520, 104.150],
];

const PROJECT_COLOR = '#f59e0b';
const GLS_COLOR = '#8b5cf6';
const SELECTED_COLOR = '#ef4444';

const COMPETITOR_COLORS = {
  ours: '#f59e0b',
  propnex: '#3b82f6',
  era: '#ef4444',
  orangetee: '#f97316',
  others: '#6b7280',
};

function computeSiteShape(lat: number, lng: number): [number, number][] {
  const d = 0.0008;
  return [
    [lat - d, lng - d * 1.2], [lat - d, lng + d * 1.2],
    [lat + d * 0.8, lng + d * 1.4], [lat + d * 1.2, lng], [lat + d * 0.8, lng - d * 1.4],
  ];
}

function parseTopYear(topDate?: number | string | null): number {
  if (!topDate) return 0;
  if (typeof topDate === 'number') return topDate > 2000 ? topDate : new Date(topDate).getFullYear();
  const m = String(topDate).match(/\b(20\d{2})\b/);
  return m ? parseInt(m[1], 10) : 0;
}

function mapDetailsToProject(d: ProjectDetails, upcomingIds?: Set<string>): MapProject | null {
  const lat = d.mapLatitude ? Number(d.mapLatitude) : null;
  const lng = d.mapLongitude ? Number(d.mapLongitude) : null;
  if (!lat || !lng || !d.id || !d.name) return null;
  const regionRaw = String(d.area ?? '').toUpperCase();
  const region: Region = (['CCR', 'RCR', 'OCR'].includes(regionRaw) ? regionRaw as Region : 'OCR');
  const catName = String(d.projectCategory ?? '').toLowerCase();
  const type: ProjectType = (catName.includes('gls') || d.name.trim().toUpperCase().endsWith('GLS')) ? 'gls' : 'new-launch';
  const totalUnits = typeof d.totalUnits === 'number' ? d.totalUnits : (parseInt(String(d.totalUnits ?? '0'), 10) || 0);
  const district = d.district ? (d.district.startsWith('D') ? d.district : `D${d.district}`) : '';
  return {
    id: d.id, name: d.name, developer: d.developer ?? 'Unknown',
    lat, lng, district, region,
    topYear: parseTopYear(d.topDate),
    totalUnits, soldUnits: d.soldUnits ?? 0, type,
    upcoming: upcomingIds ? upcomingIds.has(String(d.id)) : false,
    plot: computeSiteShape(lat, lng),
  };
}

function labelIcon(p: MapProject, isSelected: boolean, tone: 'light' | 'dark') {
  const color = isSelected ? SELECTED_COLOR : p.type === 'gls' ? GLS_COLOR : PROJECT_COLOR;
  const bg = tone === 'dark' ? 'rgba(15,23,42,0.93)' : 'rgba(255,255,255,0.96)';
  const tc = tone === 'dark' ? '#e2e8f0' : '#0f172a';
  const sc = tone === 'dark' ? 'rgba(226,232,240,0.6)' : 'rgba(15,23,42,0.5)';
  const bc = tone === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(15,23,42,0.12)';
  const available = Math.max(0, p.totalUnits - p.soldUnits);
  const soldPct = p.totalUnits > 0 ? Math.round((p.soldUnits / p.totalUnits) * 100) : 0;
  const label = p.name.length > 24 ? p.name.slice(0, 22) + '\u2026' : p.name;
  const outline = isSelected ? `outline:2px solid ${color};outline-offset:1px;` : '';
  return L.divIcon({
    className: 'sit-map-label-icon',
    html: `<div style="display:flex;align-items:flex-start;gap:5px;cursor:grab;">
      <div style="flex-shrink:0;margin-top:7px;width:10px;height:10px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 1px 5px rgba(0,0,0,0.45);"></div>
      <div style="background:${bg};border:1px solid ${bc};border-left:3px solid ${color};border-radius:6px;padding:5px 8px 5px 6px;min-width:140px;max-width:190px;box-shadow:0 3px 12px rgba(0,0,0,0.22);${outline}display:flex;gap:4px;align-items:flex-start;">
        <div style="padding-top:2px;opacity:0.35;flex-shrink:0;letter-spacing:1px;font-size:8px;line-height:1;color:${tc};">⠿</div>
        <div style="flex:1;min-width:0;">
        <div style="font-size:10.5px;font-weight:700;color:${tc};line-height:1.2;margin-bottom:1px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:174px;">${label}</div>
        <div style="font-size:9px;color:${sc};line-height:1.2;margin-bottom:3px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:174px;">${p.developer}</div>
        <div style="display:flex;align-items:center;gap:3px;flex-wrap:wrap;margin-bottom:3px;">
          <span style="font-size:8px;background:${color}33;color:${color};border:1px solid ${color}55;border-radius:3px;padding:0 4px;font-weight:700;line-height:1.7;">${p.type === 'gls' ? 'GLS' : 'NEW LAUNCH'}</span>
          ${p.upcoming ? `<span style="font-size:8px;background:#f59e0b22;color:#b45309;border:1px solid #f59e0b66;border-radius:3px;padding:0 4px;font-weight:700;line-height:1.7;">UPCOMING</span>` : ''}
          <span style="font-size:8px;color:${sc};">${p.district}</span>
          <span style="font-size:8px;color:${sc};">${p.totalUnits} units</span>
          ${p.topYear ? `<span style="font-size:8px;color:${sc};">TOP ${p.topYear}</span>` : ''}
        </div>
        <div style="display:flex;align-items:center;gap:4px;">
          <div style="flex:1;height:3px;background:rgba(127,127,127,0.18);border-radius:2px;overflow:hidden;">
            <div style="width:${soldPct}%;height:100%;background:${color};border-radius:2px;"></div>
          </div>
          <span style="font-size:8px;color:${sc};white-space:nowrap;">${available} avail</span>
        </div>
        </div>
      </div>
    </div>`,
    iconSize: [165, 72],
    iconAnchor: [5, 5],
  });
}

/**
 * Convert OneMap GeoJSON (Polygon or MultiPolygon) into the 3-level
 * positions array Leaflet expects: [polygons[ rings[ [lat,lng] ] ]].
 * GeoJSON uses [lng, lat] order; Leaflet uses [lat, lng].
 */
function geoJsonToLeafletPositions(geo: { type: string; coordinates: unknown } | null | undefined): [number, number][][][] | null {
  if (!geo || !geo.coordinates) return null;
  const coords = geo.coordinates as any;
  const ringToLatLng = (ring: any): [number, number][] =>
    Array.isArray(ring) ? ring.map((pt: any) => [Number(pt?.[1]), Number(pt?.[0])] as [number, number]) : [];
  if (geo.type === 'Polygon') {
    if (!Array.isArray(coords)) return null;
    const rings = coords.map(ringToLatLng).filter(r => r.length > 0);
    return rings.length > 0 ? [rings] : null;
  }
  if (geo.type === 'MultiPolygon') {
    if (!Array.isArray(coords)) return null;
    const polys = coords
      .map((poly: any) => (Array.isArray(poly) ? poly.map(ringToLatLng).filter((r: [number, number][]) => r.length > 0) : []))
      .filter((p: [number, number][][]) => p.length > 0);
    return polys.length > 0 ? polys : null;
  }
  return null;
}

function computeSpreadLayout(projects: MapProject[]): Map<string, [number, number]> {
  if (projects.length === 0) return new Map();
  // Force-based dispersion: each label starts at its pin and is pushed away
  // from overlapping neighbours while a spring pulls it back toward the pin.
  // This keeps labels close to their projects and avoids overlaps that the
  // previous grid-snapping approach produced when several projects clustered.
  //
  // Label footprint in lat/lng degrees, calibrated for the default Singapore
  // fit zoom (~zoom 12, where 1° ≈ 1700–2900 px depending on container).
  // Slightly larger than the rendered card so adjacent labels have a small
  // visual gap.
  const SIZE_LAT = 0.028;
  const SIZE_LNG = 0.072;
  // Keep labels within the visible Singapore viewport.
  const LAT_MIN = 1.230, LAT_MAX = 1.465;
  const LNG_MIN = 103.620, LNG_MAX = 104.030;

  const orig: Array<[number, number]> = projects.map((p) => [p.lat, p.lng]);
  // Tiny deterministic perturbation so coincident pins don't get a zero
  // separation vector (which would leave them stuck on top of each other).
  const pos: Array<[number, number]> = projects.map((p, i) => [
    p.lat + (((i * 9301 + 49297) % 233280) / 233280 - 0.5) * 1e-5,
    p.lng + (((i * 4567 + 12345) % 233280) / 233280 - 0.5) * 1e-5,
  ]);

  const ITERS = 120;
  const PULL = 0.04;        // spring strength toward original pin
  const PUSH_DAMPING = 0.9; // resolve most of the overlap each pass
  for (let iter = 0; iter < ITERS; iter++) {
    // Pairwise repulsion via minimum-translation-vector (axis-aligned boxes).
    for (let i = 0; i < pos.length; i++) {
      for (let j = i + 1; j < pos.length; j++) {
        const dLat = pos[j][0] - pos[i][0];
        const dLng = pos[j][1] - pos[i][1];
        const overlapLat = SIZE_LAT - Math.abs(dLat);
        const overlapLng = SIZE_LNG - Math.abs(dLng);
        if (overlapLat > 0 && overlapLng > 0) {
          // Push along the axis of smallest (normalised) penetration so the
          // labels separate with the least disturbance.
          const ratioLat = overlapLat / SIZE_LAT;
          const ratioLng = overlapLng / SIZE_LNG;
          if (ratioLat <= ratioLng) {
            const shift = (overlapLat / 2) * PUSH_DAMPING * (dLat >= 0 ? 1 : -1);
            pos[j][0] += shift;
            pos[i][0] -= shift;
          } else {
            const shift = (overlapLng / 2) * PUSH_DAMPING * (dLng >= 0 ? 1 : -1);
            pos[j][1] += shift;
            pos[i][1] -= shift;
          }
        }
      }
    }
    // Spring pull toward original pin position.
    for (let i = 0; i < pos.length; i++) {
      pos[i][0] += (orig[i][0] - pos[i][0]) * PULL;
      pos[i][1] += (orig[i][1] - pos[i][1]) * PULL;
    }
    // Clamp to the visible viewport (label anchor is near top-left, so leave
    // room on the right/bottom for the card to fit on-screen).
    for (let i = 0; i < pos.length; i++) {
      pos[i][0] = Math.max(LAT_MIN + SIZE_LAT, Math.min(LAT_MAX, pos[i][0]));
      pos[i][1] = Math.max(LNG_MIN, Math.min(LNG_MAX - SIZE_LNG, pos[i][1]));
    }
  }

  const result = new Map<string, [number, number]>();
  projects.forEach((p, i) => result.set(p.id, pos[i]));
  return result;
}

function pinIcon(color: string, size = 28) {
  return L.divIcon({
    className: 'sit-map-pin',
    html: `<div style="background:${color};width:${size}px;height:${size}px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.35);"></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
  });
}

function FitSingapore() {
  const map = useMap();
  React.useEffect(() => {
    map.setMaxBounds(SG_MAX_BOUNDS);
    map.setMinZoom(11);
    map.setMaxZoom(18);
    map.fitBounds(SG_BOUNDS, { padding: [20, 20] });
  }, [map]);
  return null;
}

function ZoomControlBottomRight() {
  const map = useMap();
  React.useEffect(() => {
    const control = L.control.zoom({ position: 'bottomright' });
    control.addTo(map);
    return () => {
      control.remove();
    };
  }, [map]);
  return null;
}

function AttributionInline({ attribution }: { attribution: string }) {
  const map = useMap();
  React.useEffect(() => {
    const control = L.control.attribution({ position: 'bottomleft', prefix: false });
    control.addAttribution(attribution);
    control.addTo(map);
    return () => {
      control.remove();
    };
  }, [map, attribution]);
  return null;
}

function FlyToProject({ project }: { project: MapProject | null }) {
  const map = useMap();
  const prevId = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (project && project.id !== prevId.current) {
      prevId.current = project.id;
      map.flyTo([project.lat, project.lng], Math.max(map.getZoom(), 15), { animate: true, duration: 0.8 });
    }
    if (!project) prevId.current = null;
  }, [project, map]);
  return null;
}

// ──────────────────────────────────────────────────────────────────────
// UI primitives
// ──────────────────────────────────────────────────────────────────────

interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  icon?: React.ReactNode;
}

function Segmented<T extends string>({
  value,
  onChange,
  options,
  accent = 'var(--color-accent)',
  size = 'md',
}: {
  value: T;
  onChange: (v: T) => void;
  options: SegmentedOption<T>[];
  accent?: string;
  size?: 'sm' | 'md';
}) {
  const padY = size === 'sm' ? 0.5 : 0.85;
  const padX = size === 'sm' ? 1.25 : 1.75;
  const fontSize = size === 'sm' ? '0.75rem' : '0.82rem';
  return (
    <Box sx={{
      display: 'inline-flex',
      bgcolor: 'rgba(var(--color-text-rgb), 0.06)',
      borderRadius: 999,
      p: 0.4,
      border: '1px solid rgba(var(--color-text-rgb), 0.08)',
      gap: 0.25,
    }}>
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <ButtonBase
            key={opt.value}
            onClick={() => onChange(opt.value)}
            sx={{
              px: padX,
              py: padY,
              borderRadius: 999,
              gap: 0.6,
              display: 'inline-flex',
              alignItems: 'center',
              transition: 'background-color 160ms ease, color 160ms ease',
              bgcolor: active ? accent : 'transparent',
              color: active ? '#fff' : 'rgba(var(--color-text-rgb), 0.78)',
              fontWeight: active ? 700 : 500,
              fontSize,
              boxShadow: active ? '0 2px 8px rgba(0,0,0,0.25)' : 'none',
              '&:hover': {
                bgcolor: active ? accent : 'rgba(var(--color-text-rgb), 0.08)',
                color: active ? '#fff' : 'var(--color-text-strong)',
              },
              '& svg': { fontSize: 16 },
            }}
          >
            {opt.icon}
            {opt.label}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

const ControlGroup: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Typography variant="caption" sx={{
      letterSpacing: 0.8,
      textTransform: 'uppercase',
      fontWeight: 700,
      fontSize: '0.65rem',
      color: 'rgba(var(--color-text-rgb), 0.55)',
    }}>
      {label}
    </Typography>
    {children}
  </Box>
);

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string; sub?: string }> = ({ icon, title, sub }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25 }}>
    <Box sx={{
      width: 28, height: 28, borderRadius: 1,
      bgcolor: 'rgba(var(--color-accent-rgb), 0.15)',
      color: 'var(--color-accent)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {icon}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="overline" sx={{ fontWeight: 700, lineHeight: 1, color: 'var(--color-text-strong)' }}>
        {title}
      </Typography>
      {sub && (
        <Typography variant="caption" sx={{ display: 'block', color: 'rgba(var(--color-text-rgb), 0.65)', mt: 0.25 }}>
          {sub}
        </Typography>
      )}
    </Box>
  </Box>
);

const KpiTile: React.FC<{
  icon?: React.ReactNode;
  label: string;
  value: React.ReactNode;
  sub?: React.ReactNode;
  accent?: string;
}> = ({ icon, label, value, sub, accent = 'var(--color-accent)' }) => (
  <Box sx={{
    flex: 1,
    minWidth: 0,
    p: 1.25,
    borderRadius: 1.5,
    bgcolor: 'rgba(var(--color-text-rgb), 0.04)',
    border: '1px solid rgba(var(--color-text-rgb), 0.06)',
    position: 'relative',
    overflow: 'hidden',
  }}>
    <Box sx={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: 3, bgcolor: accent }} />
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
      {icon && <Box sx={{ color: accent, display: 'flex', '& svg': { fontSize: 14 } }}>{icon}</Box>}
      <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.75)', fontWeight: 600 }}>{label}</Typography>
    </Box>
    <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--color-text-strong)', lineHeight: 1.15 }}>
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.65)' }}>{sub}</Typography>
    )}
  </Box>
);

const ProgressRow: React.FC<{ label: string; value: number; max: number; color?: string; rightLabel?: string }> = ({ label, value, max, color = 'var(--color-accent)', rightLabel }) => {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <Box sx={{ mb: 0.75 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.25 }}>
        <Typography variant="caption" sx={{ color: 'var(--color-text-strong)', fontWeight: 600 }}>{label}</Typography>
        <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.85)' }}>
          {rightLabel ?? value}
        </Typography>
      </Box>
      <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(var(--color-text-rgb), 0.08)' }}>
        <Box sx={{ width: `${pct}%`, height: '100%', borderRadius: 3, bgcolor: color, transition: 'width 240ms ease' }} />
      </Box>
    </Box>
  );
};

// Stacked horizontal bar segments [{label, value, color}]
const StackedBar: React.FC<{ segments: { label: string; value: number; color: string }[] }> = ({ segments }) => {
  const total = segments.reduce((s, x) => s + x.value, 0) || 1;
  return (
    <>
      <Box sx={{ display: 'flex', height: 14, borderRadius: 1, overflow: 'hidden' }}>
        {segments.map((s) => {
          const pct = (s.value / total) * 100;
          return (
            <Box
              key={s.label}
              sx={{ width: `${pct}%`, bgcolor: s.color, transition: 'width 240ms ease' }}
              title={`${s.label}: ${s.value} (${pct.toFixed(1)}%)`}
            />
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
        {segments.map((s) => (
          <Box key={s.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color }} />
            <Typography variant="caption" sx={{ color: 'var(--color-text-strong)' }}>
              {s.label} <strong>{((s.value / total) * 100).toFixed(0)}%</strong>
            </Typography>
          </Box>
        ))}
      </Box>
    </>
  );
};

// ──────────────────────────────────────────────────────────────────────
// Panel shell
// ──────────────────────────────────────────────────────────────────────
const PanelShell: React.FC<{
  title: React.ReactNode;
  sub?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ title, sub, onClose, children }) => (
  <Card sx={{
    position: 'absolute',
    top: 12, right: 12,
    width: { xs: '94%', sm: 440 },
    maxHeight: 'calc(100% - 24px)',
    overflowY: 'auto',
    zIndex: 500,
    bgcolor: 'var(--color-surface)',
    boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
    border: '1px solid rgba(var(--color-text-rgb), 0.08)',
    borderRadius: 2,
  }}>
    <Box sx={{
      position: 'sticky', top: 0, zIndex: 1,
      bgcolor: 'var(--color-surface)',
      borderBottom: '1px solid rgba(var(--color-text-rgb), 0.08)',
      px: 2, py: 1.5,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'var(--color-text-strong)', lineHeight: 1.2 }}>
          {title}
        </Typography>
        {sub && (
          <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', display: 'block', mt: 0.25 }}>
            {sub}
          </Typography>
        )}
      </Box>
      <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
    </Box>
    <CardContent sx={{ p: 2 }}>
      {children}
    </CardContent>
  </Card>
);

// ──────────────────────────────────────────────────────────────────────
// Project info (read-only) for Project View
// ──────────────────────────────────────────────────────────────────────
const ProjectInfoPanel: React.FC<{ project: MapProject; onClose: () => void }> = ({ project, onClose }) => {
  const soldPct = project.totalUnits ? Math.round((project.soldUnits / project.totalUnits) * 100) : 0;
  const available = Math.max(0, project.totalUnits - project.soldUnits);
  const subParts = [project.district, project.region, project.topYear ? `TOP ${project.topYear}` : null].filter(Boolean);
  return (
    <PanelShell
      title={project.name}
      sub={subParts.join(' · ')}
      onClose={onClose}
    >
      <Stack direction="row" spacing={0.75} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 0.75 }}>
        <Chip size="small" color={project.type === 'gls' ? 'secondary' : 'warning'}
          label={project.type === 'gls' ? 'GLS' : 'New Launch'} />
        {project.district && <Chip size="small" label={project.district} />}
        {project.region && <Chip size="small" label={project.region} />}
        {project.topYear ? <Chip size="small" label={`TOP ${project.topYear}`} /> : null}
      </Stack>
      <Typography variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.85)', mb: 2 }}>
        Developer: <strong style={{ color: 'var(--color-text-strong)' }}>{project.developer}</strong>
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <KpiTile label="Total Units" value={project.totalUnits.toLocaleString()} accent="#3b82f6" />
        <KpiTile label="Sold" value={project.soldUnits.toLocaleString()} sub={`${soldPct}%`} accent="#10b981" />
        <KpiTile label="Available" value={available.toLocaleString()} sub={`${100 - soldPct}%`} accent="#f59e0b" />
      </Box>
      <ProgressRow
        label="Sales progress"
        value={soldPct}
        max={100}
        color="#10b981"
        rightLabel={`${soldPct}% sold`}
      />
    </PanelShell>
  );
};

// ──────────────────────────────────────────────────────────────────────
// Project Situation Picture panel — tabbed: Project Tx | 5km Tx
// ──────────────────────────────────────────────────────────────────────

type TxBreakdown = { prelaunch: number; postLaunch: number; periods: Array<{ label: string; count: number }> };
type AgentDetailAgent = {
  name: string;
  ceaNumber: string;
  mobile: string | null;
  email: string | null;
  photo: string | null;
  designation: string | null;
  transactionCount: number;
  latestOptionDate: string | null;
};
type AgentDivisionDetail = { divisionName: string; count: number; agents: AgentDetailAgent[] };
type AgentDetail = {
  agentsWithTransaction: number;
  byDivision: AgentDivisionDetail[];
  periodBreakdown: Array<{ label: string; count: number }>;
};

const DIVISION_COLORS_PANEL = ['#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#ef4444', '#06b6d4', '#84cc16'];

const escapeCsvField = (value: string | number | null): string => {
  const text = value === null ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

const downloadAgentCsv = (projectName: string, radiusKm: number, divisions: AgentDivisionDetail[]) => {
  const rows = divisions.flatMap((division) => division.agents.map((agent) => [
    division.divisionName,
    agent.name,
    agent.ceaNumber,
    agent.designation,
    agent.mobile,
    agent.email,
    agent.transactionCount,
    agent.latestOptionDate ? new Date(agent.latestOptionDate).toLocaleDateString('en-SG') : '',
  ]));
  const headers = ['Division', 'Agent Name', 'CEA Number', 'Designation', 'Mobile', 'Email', `Transactions Within ${radiusKm}km`, 'Latest Option Date'];
  const csv = [headers, ...rows].map((row) => row.map(escapeCsvField).join(',')).join('\r\n');
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const safeProjectName = projectName.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase() || 'project';
  link.href = url;
  link.download = `${safeProjectName}-${radiusKm}km-agents-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
};

const AgentDivisionDrillList: React.FC<{
  divisions: AgentDivisionDetail[];
  expandedDivision: string | null;
  onToggle: (divisionName: string) => void;
}> = ({ divisions, expandedDivision, onToggle }) => {
  const max = divisions.length > 0 ? Math.max(...divisions.map((division) => division.count), 1) : 1;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.65 }}>
      {divisions.map((division, index) => {
        const expanded = expandedDivision === division.divisionName;
        const color = DIVISION_COLORS_PANEL[index % DIVISION_COLORS_PANEL.length];
        return (
          <Box key={division.divisionName} sx={{ borderRadius: 1.5, border: `1px solid ${expanded ? `${color}55` : 'rgba(var(--color-text-rgb), 0.07)'}`, overflow: 'hidden' }}>
            <ButtonBase
              onClick={() => onToggle(division.divisionName)}
              aria-expanded={expanded}
              sx={{ width: '100%', display: 'block', px: 1, py: 0.8, textAlign: 'left', '&:hover': { bgcolor: 'rgba(var(--color-text-rgb), 0.035)' } }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.55 }}>
                <Typography sx={{ flex: 1, fontSize: '0.72rem', fontWeight: 800, color: 'var(--color-text-strong)' }}>{division.divisionName}</Typography>
                <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'rgba(var(--color-text-rgb), 0.72)' }}>{division.count} agents</Typography>
                <ExpandMoreIcon sx={{ fontSize: 17, color, transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 180ms ease' }} />
              </Box>
              <Box sx={{ height: 5, borderRadius: 3, bgcolor: 'rgba(var(--color-text-rgb), 0.08)' }}>
                <Box sx={{ width: `${(division.count / max) * 100}%`, height: '100%', borderRadius: 3, bgcolor: color }} />
              </Box>
            </ButtonBase>
            <Collapse in={expanded} unmountOnExit>
              <Box sx={{ p: 0.8, pt: 0, display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                {division.agents.map((agent) => (
                  <Box key={agent.ceaNumber} sx={{ display: 'flex', gap: 0.85, p: 0.85, borderRadius: 1.25, bgcolor: 'rgba(var(--color-text-rgb), 0.035)', border: '1px solid rgba(var(--color-text-rgb), 0.06)' }}>
                    <Avatar src={agent.photo ?? undefined} alt={agent.name} sx={{ width: 34, height: 34, bgcolor: `${color}33`, color, fontSize: '0.72rem', fontWeight: 800 }}>
                      {agent.name.split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 0.75 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--color-text-strong)', lineHeight: 1.2 }}>{agent.name}</Typography>
                          <Typography sx={{ fontSize: '0.61rem', color: 'rgba(var(--color-text-rgb), 0.6)', mt: 0.15 }}>
                            {[agent.designation, agent.ceaNumber].filter(Boolean).join(' · ')}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                          <Typography sx={{ fontSize: '0.78rem', fontWeight: 900, color }}>{agent.transactionCount}</Typography>
                          <Typography sx={{ fontSize: '0.56rem', color: 'rgba(var(--color-text-rgb), 0.52)' }}>transactions</Typography>
                        </Box>
                      </Box>
                      {agent.latestOptionDate && (
                        <Typography sx={{ fontSize: '0.59rem', color: 'rgba(var(--color-text-rgb), 0.58)', mt: 0.45 }}>
                          Latest option: {new Date(agent.latestOptionDate).toLocaleDateString('en-SG')}
                        </Typography>
                      )}
                      {(agent.mobile || agent.email) && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 0.45 }}>
                          {agent.mobile && <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.25, fontSize: '0.59rem', color: 'rgba(var(--color-text-rgb), 0.68)' }}><PhoneIcon sx={{ fontSize: 11 }} />{agent.mobile}</Typography>}
                          {agent.email && <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.25, fontSize: '0.59rem', color: 'rgba(var(--color-text-rgb), 0.68)', wordBreak: 'break-all' }}><EmailIcon sx={{ fontSize: 11 }} />{agent.email}</Typography>}
                        </Box>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Box>
  );
};

const DrillInShell: React.FC<{ title: string; accentColor: string; onClose: () => void; action?: React.ReactNode; children: React.ReactNode }> = ({ title, accentColor, onClose, action, children }) => (
  <Box sx={{
    mt: 1.5, borderRadius: 2,
    border: `1px solid ${accentColor}33`,
    bgcolor: 'rgba(var(--color-text-rgb), 0.02)',
    overflow: 'hidden',
  }}>
    <Box sx={{
      px: 1.5, py: 1,
      borderBottom: '1px solid rgba(var(--color-text-rgb), 0.07)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      background: `linear-gradient(90deg, ${accentColor}18, transparent)`,
    }}>
      <Typography variant="caption" sx={{ fontWeight: 800, color: accentColor, textTransform: 'uppercase', letterSpacing: 0.7, fontSize: '0.68rem' }}>
        {title}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {action}
        <IconButton size="small" onClick={onClose} sx={{ p: 0.25, '& svg': { fontSize: 14 }, color: 'rgba(var(--color-text-rgb), 0.55)' }}>
          <CloseIcon />
        </IconButton>
      </Box>
    </Box>
    <Box sx={{ p: 1.5 }}>
      {children}
    </Box>
  </Box>
);

const ProjectCopPanel: React.FC<{ project: MapProject; onClose: () => void }> = ({ project, onClose }) => {
  const [activeTab, setActiveTab] = useState<'project' | 'radius'>('project');

  const [radiusStats, setRadiusStats] = useState<{
    project: { oursTx: number; eraTx: number; totalMarket: number };
    radius: { oursTx: number; totalMarket: number; projectCount: number };
  } | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const [agentStats, setAgentStats] = useState<{
    radiusKm: number;
    agentsWithTransaction: number;
    byDivision: AgentDivisionDetail[];
  } | null>(null);
  const [agentStatsLoading, setAgentStatsLoading] = useState(true);

  type DrillScope = 'era' | 'market' | 'agent' | 'radius-agent';
  const [drillScope, setDrillScope] = useState<DrillScope | null>(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [huttonsBd, setHuttonsBd] = useState<TxBreakdown | null>(null);
  const [drillBd, setDrillBd] = useState<TxBreakdown | null>(null);
  const [agentDetail, setAgentDetail] = useState<AgentDetail | null>(null);
  const [agentDetailLoading, setAgentDetailLoading] = useState(false);
  const [expandedDivision, setExpandedDivision] = useState<string | null>(null);
  const [expandedRadiusDivision, setExpandedRadiusDivision] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setStatsLoading(true);
    setRadiusStats(null);
    setDrillScope(null);
    setExpandedDivision(null);
    setExpandedRadiusDivision(null);
    api.getSituationMapRadiusStats(project.id).then((data) => {
      if (!cancelled) { setRadiusStats(data); setStatsLoading(false); }
    }).catch((err) => {
      console.warn('ProjectCopPanel: radius stats failed', err);
      if (!cancelled) { setStatsLoading(false); }
    });

    setAgentStatsLoading(true);
    setAgentStats(null);
    api.getSituationMapAgentStats(project.id).then((data) => {
      if (!cancelled) { setAgentStats(data); setAgentStatsLoading(false); }
    }).catch((err) => {
      console.warn('ProjectCopPanel: agent stats failed', err);
      if (!cancelled) { setAgentStatsLoading(false); }
    });

    return () => { cancelled = true; };
  }, [project.id]);

  const handleDrillIn = useCallback(async (scope: 'era' | 'market') => {
    if (drillScope === scope) { setDrillScope(null); return; }
    setDrillScope(scope);
    setDrillLoading(true);
    setHuttonsBd(null);
    setDrillBd(null);
    try {
      const [hd, dd] = await Promise.all([
        api.getSituationMapProjectTxBreakdown(project.id, 'huttons'),
        api.getSituationMapProjectTxBreakdown(project.id, scope),
      ]);
      setHuttonsBd(hd);
      setDrillBd(dd);
    } catch (err) {
      console.warn('ProjectCopPanel: drill-in failed', err);
    } finally {
      setDrillLoading(false);
    }
  }, [project.id, drillScope]);

  const handleAgentDetail = useCallback(async () => {
    if (drillScope === 'agent') { setDrillScope(null); setExpandedDivision(null); return; }
    setDrillScope('agent');
    setExpandedDivision(null);
    setAgentDetailLoading(true);
    setAgentDetail(null);
    try {
      const data = await api.getSituationMapProjectAgentDetail(project.id);
      setAgentDetail(data);
    } catch (err) {
      console.warn('ProjectCopPanel: agent detail failed', err);
    } finally {
      setAgentDetailLoading(false);
    }
  }, [project.id, drillScope]);

  const handleRadiusAgentDetail = useCallback(() => {
    setDrillScope((current) => current === 'radius-agent' ? null : 'radius-agent');
    setExpandedRadiusDivision(null);
  }, []);

  const projOursTx   = radiusStats?.project.oursTx    ?? 0;
  const projEraTx    = radiusStats?.project.eraTx     ?? 0;
  const projTotal    = radiusStats?.project.totalMarket ?? 0;
  const projMarketTx = Math.max(0, projTotal - projOursTx);
  const projPct       = projTotal > 0 ? ((projOursTx   / projTotal) * 100).toFixed(1) : '0';
  const projEraPct    = projTotal > 0 ? ((projEraTx    / projTotal) * 100).toFixed(1) : '0';
  const projMarketPct = projTotal > 0 ? ((projMarketTx / projTotal) * 100).toFixed(1) : '0';
  const radOursTx    = radiusStats?.radius.oursTx     ?? 0;
  const radTotal     = radiusStats?.radius.totalMarket ?? 0;
  const radPct       = radTotal  > 0 ? ((radOursTx  / radTotal)  * 100).toFixed(1) : '0';

  const isSoldOut   = project.totalUnits > 0 && project.soldUnits >= project.totalUnits;
  const soldAllPctRaw = project.totalUnits > 0 ? (project.soldUnits / project.totalUnits) * 100 : 0;
  // Show 1 decimal when near 100% but not actually sold out (avoids misleading "100%")
  const soldAllPct = isSoldOut
    ? '100'
    : soldAllPctRaw >= 99.5
      ? soldAllPctRaw.toFixed(1)
      : String(Math.round(soldAllPctRaw));

  const metaParts = [
    project.district,
    project.region,
    project.type === 'gls' ? 'GLS' : 'New Launch',
    project.topYear ? `TOP ${project.topYear}` : null,
  ].filter(Boolean);

  return (
    <PanelShell
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <span>{project.name}</span>
          <Box component="span" sx={{
            fontSize: '0.75rem', fontWeight: 800, letterSpacing: 0.1,
            px: 1, py: 0.3, borderRadius: 999,
            bgcolor: isSoldOut ? '#10b981' : 'rgba(245,158,11,0.18)',
            color:   isSoldOut ? '#fff'     : '#b45309',
            border:  `1.5px solid ${isSoldOut ? '#059669' : 'rgba(180,83,9,0.35)'}`,
            whiteSpace: 'nowrap',
            boxShadow: isSoldOut ? '0 1px 6px rgba(16,185,129,0.35)' : 'none',
          }}>
            {project.soldUnits}/{project.totalUnits} sold · {soldAllPct}%
          </Box>
        </Box>
      }
      sub={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
          {metaParts.map((part, i) => (
            <React.Fragment key={String(part)}>
              {i > 0 && <Box component="span" sx={{ color: 'rgba(var(--color-text-rgb), 0.3)', mx: 0.25 }}>·</Box>}
              <Box component="span" sx={{
                fontSize: '0.72rem', fontWeight: i === 2 ? 700 : 500,
                color: i === 2
                  ? (project.type === 'gls' ? '#a855f7' : '#f59e0b')
                  : 'rgba(var(--color-text-rgb), 0.65)',
                bgcolor: i === 2
                  ? (project.type === 'gls' ? 'rgba(168,85,247,0.12)' : 'rgba(245,158,11,0.12)')
                  : 'transparent',
                borderRadius: i === 2 ? 0.75 : 0,
                px: i === 2 ? 0.6 : 0,
                py: i === 2 ? 0.1 : 0,
              }}>
                {part}
              </Box>
            </React.Fragment>
          ))}
        </Box>
      }
      onClose={onClose}
    >
      {/* ── Tab switcher ── */}
      <Box sx={{ mb: 2.5 }}>
        <Segmented<'project' | 'radius'>
          value={activeTab}
          onChange={(t) => {
            setActiveTab(t);
            setDrillScope(null);
            setExpandedDivision(null);
            setExpandedRadiusDivision(null);
          }}
          options={[
            { value: 'project', label: 'Project Transactions', icon: <TrendingUpIcon /> },
            { value: 'radius', label: '5km Transactions', icon: <InsightsIcon /> },
          ]}
        />
      </Box>

      {/* ════════════════════════════════════════════════
          TAB 1: PROJECT TRANSACTIONS
          ════════════════════════════════════════════════ */}
      {activeTab === 'project' && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
            <Box sx={{ width: 3, height: 18, borderRadius: 1.5, bgcolor: 'var(--color-accent)' }} />
            <Typography variant="overline" sx={{ fontWeight: 700, fontSize: '0.68rem', color: 'rgba(var(--color-text-rgb), 0.75)', letterSpacing: 1 }}>
              Project Transactions
            </Typography>
          </Box>

          {statsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={22} /></Box>
          ) : (
            <>
              {/* ── 3-column KPI grid ── */}
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0.75, mb: 1.5 }}>
                {/* Huttons – clickable → agent drill-in */}
                <Box
                  onClick={handleAgentDetail}
                  sx={{
                    p: 1.25, borderRadius: 2, cursor: 'pointer', position: 'relative', overflow: 'hidden',
                    background: drillScope === 'agent'
                      ? 'linear-gradient(135deg, rgba(245,158,11,0.2) 0%, rgba(245,158,11,0.07) 100%)'
                      : 'linear-gradient(135deg, rgba(245,158,11,0.12) 0%, rgba(245,158,11,0.03) 100%)',
                    border: drillScope === 'agent' ? '1.5px solid rgba(245,158,11,0.6)' : '1px solid rgba(245,158,11,0.22)',
                    transition: 'all 160ms ease',
                    '&:hover': { border: '1.5px solid rgba(245,158,11,0.5)', transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(245,158,11,0.15)' },
                  }}
                >
                  <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2.5, background: 'linear-gradient(90deg, #f59e0b, #fbbf24)' }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.3 }}>
                    <EmojiEventsIcon sx={{ fontSize: 11, color: '#f59e0b' }} />
                    <Typography sx={{ fontSize: '0.63rem', fontWeight: 800, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: 0.4 }}>Huttons</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '1.6rem', fontWeight: 900, color: 'var(--color-text-strong)', lineHeight: 1, mb: 0.25 }}>
                    {projOursTx}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#f59e0b', lineHeight: 1.2 }}>
                      {projPct}%
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: 'rgba(var(--color-text-rgb), 0.55)', lineHeight: 1.2 }}>mkt</Typography>
                  </Box>
                  <Box sx={{ mt: 0.5, fontSize: '0.6rem', color: '#f59e0b', fontWeight: 700, opacity: 0.85 }}>
                    agents ↓
                  </Box>
                </Box>

                {/* ERA – clickable → comparison drill-in */}
                <Box
                  onClick={() => handleDrillIn('era')}
                  sx={{
                    p: 1.25, borderRadius: 2, cursor: 'pointer', position: 'relative', overflow: 'hidden',
                    background: drillScope === 'era'
                      ? 'linear-gradient(135deg, rgba(239,68,68,0.18) 0%, rgba(239,68,68,0.06) 100%)'
                      : 'linear-gradient(135deg, rgba(239,68,68,0.1) 0%, rgba(239,68,68,0.02) 100%)',
                    border: drillScope === 'era' ? '1.5px solid rgba(239,68,68,0.6)' : '1px solid rgba(239,68,68,0.2)',
                    transition: 'all 160ms ease',
                    '&:hover': { border: '1.5px solid rgba(239,68,68,0.5)', transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(239,68,68,0.12)' },
                  }}
                >
                  <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2.5, background: 'linear-gradient(90deg, #ef4444, #f87171)' }} />
                  <Typography sx={{ fontSize: '0.63rem', fontWeight: 800, color: '#ef4444', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.3 }}>ERA</Typography>
                  <Typography sx={{ fontSize: '1.6rem', fontWeight: 900, color: 'var(--color-text-strong)', lineHeight: 1, mb: 0.25 }}>
                    {projEraTx}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#ef4444', lineHeight: 1.2 }}>
                      {projEraPct}%
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: 'rgba(var(--color-text-rgb), 0.55)', lineHeight: 1.2 }}>mkt</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.6rem', color: 'rgba(var(--color-text-rgb), 0.45)', mt: 0.25 }}>tap to drill in</Typography>
                </Box>

                {/* Market (excl. Huttons) – clickable → comparison drill-in */}
                <Box
                  onClick={() => handleDrillIn('market')}
                  sx={{
                    p: 1.25, borderRadius: 2, cursor: 'pointer', position: 'relative', overflow: 'hidden',
                    background: drillScope === 'market'
                      ? 'linear-gradient(135deg, rgba(107,114,128,0.18) 0%, rgba(107,114,128,0.06) 100%)'
                      : 'linear-gradient(135deg, rgba(107,114,128,0.1) 0%, rgba(107,114,128,0.02) 100%)',
                    border: drillScope === 'market' ? '1.5px solid rgba(107,114,128,0.55)' : '1px solid rgba(107,114,128,0.18)',
                    transition: 'all 160ms ease',
                    '&:hover': { border: '1.5px solid rgba(107,114,128,0.45)', transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(107,114,128,0.1)' },
                  }}
                >
                  <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2.5, background: 'linear-gradient(90deg, #6b7280, #9ca3af)' }} />
                  <Typography sx={{ fontSize: '0.63rem', fontWeight: 800, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.3 }}>Market</Typography>
                  <Typography sx={{ fontSize: '1.6rem', fontWeight: 900, color: 'var(--color-text-strong)', lineHeight: 1, mb: 0.25 }}>
                    {projMarketTx}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.3 }}>
                    <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#9ca3af', lineHeight: 1.2 }}>
                      {projMarketPct}%
                    </Typography>
                    <Typography sx={{ fontSize: '0.65rem', color: 'rgba(var(--color-text-rgb), 0.55)', lineHeight: 1.2 }}>mkt</Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.6rem', color: 'rgba(var(--color-text-rgb), 0.45)', mt: 0.25 }}>excl. Huttons</Typography>
                </Box>
              </Box>

              {/* Stacked share bar */}
              {projTotal > 0 && (
                <Box sx={{ mb: 2 }}>
                  <StackedBar segments={[
                    { label: 'Huttons', value: projOursTx, color: '#f59e0b' },
                    { label: 'ERA',     value: projEraTx,  color: '#ef4444' },
                    { label: 'Others',  value: Math.max(0, projMarketTx - projEraTx), color: '#6b7280' },
                  ]} />
                </Box>
              )}

              {/* ── ERA / Market drill-in panel ── */}
              {(drillScope === 'era' || drillScope === 'market') && (
                <DrillInShell
                  title={`Our vs ${drillScope === 'era' ? 'ERA' : 'Market'}`}
                  accentColor={drillScope === 'era' ? '#ef4444' : '#6b7280'}
                  onClose={() => setDrillScope(null)}
                >
                  {drillLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2.5 }}><CircularProgress size={20} /></Box>
                  ) : huttonsBd && drillBd ? (
                    <>
                      {/* By Total Sale section */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <Box sx={{ width: 3, height: 12, borderRadius: 1, bgcolor: 'var(--color-accent)' }} />
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: 'rgba(var(--color-text-rgb), 0.8)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                          By Total Sale
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, mb: 2 }}>
                        {[
                          { phase: 'Prelaunch',   ours: huttonsBd.prelaunch,  them: drillBd.prelaunch },
                          { phase: 'Post Launch', ours: huttonsBd.postLaunch, them: drillBd.postLaunch },
                        ].map(({ phase, ours, them }) => {
                          const compColor = drillScope === 'era' ? '#ef4444' : '#6b7280';
                          const compLabel = drillScope === 'era' ? 'ERA' : 'Mkt';
                          const maxV = Math.max(ours, them, 1);
                          const oursPct  = projTotal > 0 ? ((ours  / projTotal) * 100).toFixed(1) : '0';
                          const themsPct = projTotal > 0 ? ((them  / projTotal) * 100).toFixed(1) : '0';
                          return (
                            <Box key={phase} sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'rgba(var(--color-text-rgb), 0.04)', border: '1px solid rgba(var(--color-text-rgb), 0.07)' }}>
                              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'rgba(var(--color-text-rgb), 0.7)', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>{phase}</Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                {/* Ours row */}
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.4 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#f59e0b' }}>Ours</Typography>
                                      <Typography sx={{ fontSize: '1.15rem', fontWeight: 900, color: '#f59e0b', lineHeight: 1 }}>{ours}</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#f59e0b' }}>{oursPct}%</Typography>
                                  </Box>
                                  <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(var(--color-text-rgb), 0.08)' }}>
                                    <Box sx={{ width: `${(ours / maxV) * 100}%`, height: '100%', borderRadius: 3, background: 'linear-gradient(90deg, #f59e0b, #fbbf24)' }} />
                                  </Box>
                                </Box>
                                {/* Competitor row */}
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.4 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: compColor }}>{compLabel}</Typography>
                                      <Typography sx={{ fontSize: '1.15rem', fontWeight: 900, color: compColor, lineHeight: 1 }}>{them}</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: compColor }}>{themsPct}%</Typography>
                                  </Box>
                                  <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(var(--color-text-rgb), 0.08)' }}>
                                    <Box sx={{ width: `${(them / maxV) * 100}%`, height: '100%', borderRadius: 3, bgcolor: compColor }} />
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>

                      {/* By Period section */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <Box sx={{ width: 3, height: 12, borderRadius: 1, bgcolor: 'var(--color-accent)' }} />
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: 'rgba(var(--color-text-rgb), 0.8)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
                          By Period
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                        {huttonsBd.periods.map((p, i) => {
                          const tp = drillBd.periods[i];
                          const themCount = tp?.count ?? 0;
                          const compColor = drillScope === 'era' ? '#ef4444' : '#6b7280';
                          const compLabel = drillScope === 'era' ? 'ERA' : 'Mkt';
                          const maxV = Math.max(p.count, themCount, 1);
                          const oursPct  = projTotal > 0 ? ((p.count   / projTotal) * 100).toFixed(1) : '0';
                          const themsPct = projTotal > 0 ? ((themCount / projTotal) * 100).toFixed(1) : '0';
                          return (
                            <Box key={p.label} sx={{ p: 1.25, borderRadius: 1.5, bgcolor: 'rgba(var(--color-text-rgb), 0.03)', border: '1px solid rgba(var(--color-text-rgb), 0.06)' }}>
                              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'rgba(var(--color-text-rgb), 0.7)', mb: 0.85 }}>{p.label}</Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                {/* Ours row */}
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.4 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: '#f59e0b' }}>Ours</Typography>
                                      <Typography sx={{ fontSize: '1.15rem', fontWeight: 900, color: '#f59e0b', lineHeight: 1 }}>{p.count}</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#f59e0b' }}>{oursPct}%</Typography>
                                  </Box>
                                  <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(var(--color-text-rgb), 0.08)' }}>
                                    <Box sx={{ width: `${(p.count / maxV) * 100}%`, height: '100%', borderRadius: 3, background: 'linear-gradient(90deg, #f59e0b, #fbbf24)' }} />
                                  </Box>
                                </Box>
                                {/* Competitor row */}
                                <Box>
                                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 0.4 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: compColor }}>{compLabel}</Typography>
                                      <Typography sx={{ fontSize: '1.15rem', fontWeight: 900, color: compColor, lineHeight: 1 }}>{themCount}</Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: compColor }}>{themsPct}%</Typography>
                                  </Box>
                                  <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(var(--color-text-rgb), 0.08)' }}>
                                    <Box sx={{ width: `${(themCount / maxV) * 100}%`, height: '100%', borderRadius: 3, bgcolor: compColor }} />
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    </>
                  ) : null}
                </DrillInShell>
              )}

              {/* ── Huttons agent drill-in panel ── */}
              {drillScope === 'agent' && (
                <DrillInShell title="Agents w/ Transaction" accentColor="#f59e0b" onClose={() => { setDrillScope(null); setExpandedDivision(null); }}>
                  {agentDetailLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2.5 }}><CircularProgress size={20} /></Box>
                  ) : agentDetail ? (
                    <>
                      {/* Total agents badge */}
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, p: 1,
                        borderRadius: 1.5, background: 'linear-gradient(135deg, rgba(245,158,11,0.1), rgba(245,158,11,0.03))',
                        border: '1px solid rgba(245,158,11,0.25)',
                      }}>
                        <GroupsIcon sx={{ color: '#f59e0b', fontSize: 22 }} />
                        <Box>
                          <Typography sx={{ fontSize: '1.5rem', fontWeight: 900, color: '#f59e0b', lineHeight: 1 }}>{agentDetail.agentsWithTransaction}</Typography>
                          <Typography sx={{ fontSize: '0.65rem', color: 'rgba(var(--color-text-rgb), 0.65)' }}>Huttons agents in this project</Typography>
                        </Box>
                      </Box>

                      {/* Division breakdown */}
                      {agentDetail.byDivision.length > 0 && (
                        <>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                            <Box sx={{ width: 3, height: 12, borderRadius: 1, bgcolor: '#f59e0b' }} />
                            <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: 'rgba(var(--color-text-rgb), 0.75)', textTransform: 'uppercase', letterSpacing: 0.6 }}>By Division</Typography>
                          </Box>
                          <Box sx={{ mb: 1.5 }}>
                            <AgentDivisionDrillList
                              divisions={agentDetail.byDivision}
                              expandedDivision={expandedDivision}
                              onToggle={(divisionName) => setExpandedDivision((current) => current === divisionName ? null : divisionName)}
                            />
                          </Box>
                        </>
                      )}

                      {/* Period breakdown */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
                        <Box sx={{ width: 3, height: 12, borderRadius: 1, bgcolor: '#f59e0b' }} />
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: 'rgba(var(--color-text-rgb), 0.75)', textTransform: 'uppercase', letterSpacing: 0.6 }}>By Period</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {agentDetail.periodBreakdown.map((p, i) => (
                          <Box key={p.label} sx={{
                            display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.75,
                            borderRadius: 1.5, bgcolor: 'rgba(var(--color-text-rgb), 0.03)', border: '1px solid rgba(var(--color-text-rgb), 0.06)',
                          }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: DIVISION_COLORS_PANEL[i % DIVISION_COLORS_PANEL.length], flexShrink: 0 }} />
                            <Typography sx={{ flex: 1, fontSize: '0.68rem', color: 'rgba(var(--color-text-rgb), 0.7)' }}>{p.label}</Typography>
                            <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: 'var(--color-text-strong)' }}>{p.count}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </>
                  ) : null}
                </DrillInShell>
              )}
            </>
          )}
        </>
      )}

      {/* ════════════════════════════════════════════════
          TAB 2: 5KM TRANSACTIONS
          ════════════════════════════════════════════════ */}
      {activeTab === 'radius' && (
        <>
          <SectionHeader
            icon={<TrendingUpIcon fontSize="small" />}
            title="Within 5km Transactions"
            sub={`within 5km of ${project.name}${radiusStats ? ` · ${radiusStats.radius.projectCount} projects` : ''}`}
          />
          {statsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <KpiTile icon={<EmojiEventsIcon />} label="Our (Huttons)" value={radOursTx}
                sub={`${radPct}% of 5km market`} accent={COMPETITOR_COLORS.ours} />
              <KpiTile label="Total Market" value={radTotal} sub="all agencies" accent="#6b7280" />
            </Box>
          )}

          <Divider sx={{ my: 1.5 }} />

          <SectionHeader icon={<GroupsIcon fontSize="small" />} title="Agents w/ Transaction"
            sub="within 5km · Huttons agents" />
          {agentStatsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>
          ) : (
            <>
              <ButtonBase
                onClick={handleRadiusAgentDetail}
                sx={{
                  width: '100%', display: 'block', textAlign: 'left', mb: 1.5, p: 1.25,
                  borderRadius: 1.5, border: drillScope === 'radius-agent' ? '1.5px solid rgba(16,185,129,0.65)' : '1px solid rgba(16,185,129,0.25)',
                  background: drillScope === 'radius-agent' ? 'rgba(16,185,129,0.12)' : 'rgba(16,185,129,0.045)',
                  '&:hover': { bgcolor: 'rgba(16,185,129,0.1)', transform: 'translateY(-1px)' },
                  transition: 'all 160ms ease',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ReceiptIcon sx={{ color: '#10b981', fontSize: 18 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: 'rgba(var(--color-text-rgb), 0.75)' }}>Agents w/ Transaction</Typography>
                    <Typography sx={{ fontSize: '1.35rem', fontWeight: 900, color: 'var(--color-text-strong)', lineHeight: 1.15 }}>{agentStats?.agentsWithTransaction ?? 0}</Typography>
                    <Typography sx={{ fontSize: '0.62rem', color: 'rgba(var(--color-text-rgb), 0.58)' }}>within {agentStats?.radiusKm ?? 5}km · tap to see agent details</Typography>
                  </Box>
                  <ExpandMoreIcon sx={{ color: '#10b981', transform: drillScope === 'radius-agent' ? 'rotate(180deg)' : 'none', transition: 'transform 180ms ease' }} />
                </Box>
              </ButtonBase>

              {drillScope === 'radius-agent' && agentStats && (
                <DrillInShell
                  title={`${agentStats.radiusKm}km Agent Details`}
                  accentColor="#10b981"
                  onClose={() => { setDrillScope(null); setExpandedRadiusDivision(null); }}
                  action={(
                    <ButtonBase
                      onClick={() => downloadAgentCsv(project.name, agentStats.radiusKm, agentStats.byDivision)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.35, px: 0.7, py: 0.35, borderRadius: 1, color: '#10b981', fontSize: '0.61rem', fontWeight: 800, '&:hover': { bgcolor: 'rgba(16,185,129,0.12)' } }}
                    >
                      <DownloadIcon sx={{ fontSize: 14 }} /> Export CSV
                    </ButtonBase>
                  )}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25, p: 1, borderRadius: 1.5, bgcolor: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)' }}>
                    <GroupsIcon sx={{ color: '#10b981', fontSize: 21 }} />
                    <Box>
                      <Typography sx={{ fontSize: '1.35rem', fontWeight: 900, color: '#10b981', lineHeight: 1 }}>{agentStats.agentsWithTransaction}</Typography>
                      <Typography sx={{ fontSize: '0.62rem', color: 'rgba(var(--color-text-rgb), 0.62)' }}>Huttons agents within {agentStats.radiusKm}km</Typography>
                    </Box>
                  </Box>
                  <AgentDivisionDrillList
                    divisions={agentStats.byDivision}
                    expandedDivision={expandedRadiusDivision}
                    onToggle={(divisionName) => setExpandedRadiusDivision((current) => current === divisionName ? null : divisionName)}
                  />
                </DrillInShell>
              )}
            </>
          )}
        </>
      )}
    </PanelShell>
  );
};

// ──────────────────────────────────────────────────────────────────────
// District Situation Picture panel — transactions / listings + agent profile
// ──────────────────────────────────────────────────────────────────────
const DistrictCopPanel: React.FC<{ district: DistrictInfo; metric: HeatMetric; sgTransactionsTotal: number; onClose: () => void }> = ({ district, metric, sgTransactionsTotal, onClose }) => {
  const txCount = district.metrics.transactions;
  const txSharePct = sgTransactionsTotal > 0 ? Math.round((txCount / sgTransactionsTotal) * 1000) / 10 : 0;

  const [agentStats, setAgentStats] = useState<{
    agentsWithTransaction: number;
    byDivision: Array<{ divisionName: string; count: number }>;
  } | null>(null);
  const [agentStatsLoading, setAgentStatsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setAgentStatsLoading(true);
    setAgentStats(null);
    api.getSituationMapDistrictAgentStats(district.code).then((data) => {
      if (!cancelled) { setAgentStats(data); setAgentStatsLoading(false); }
    }).catch((err) => {
      console.warn('DistrictCopPanel: agent stats failed', err);
      if (!cancelled) { setAgentStatsLoading(false); }
    });
    return () => { cancelled = true; };
  }, [district.code]);

  const divMax = agentStats?.byDivision.length
    ? Math.max(...agentStats.byDivision.map((d) => d.count))
    : 1;
  const DIVISION_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#ef4444', '#06b6d4', '#84cc16'];

  return (
    <PanelShell
      title={`${district.code} · ${district.name}`}
      sub={
        <span>
          Region: {district.region} · Shading: <strong style={{ color: 'var(--color-accent)' }}>{metric}</strong>
        </span>
      }
      onClose={onClose}
    >
      <SectionHeader
        icon={<InsightsIcon fontSize="small" />}
        title="Market Activity"
        sub="Share against Singapore (whole market)"
      />

      <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
        <KpiTile
          icon={<ReceiptIcon />}
          label="Transactions"
          value={txCount}
          sub={sgTransactionsTotal > 0 ? `${txSharePct.toFixed(1)}% of SG (${sgTransactionsTotal.toLocaleString()})` : undefined}
          accent="#10b981"
        />
        <KpiTile icon={<ListIcon />} label="Listings" value="–" accent="#3b82f6" />
      </Box>

      {sgTransactionsTotal > 0 && (
        <ProgressRow label="Transactions" value={txSharePct} max={100} color="#10b981" rightLabel={`${txSharePct.toFixed(1)}%`} />
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
        <KpiTile label="EOI" value="–" accent="#f59e0b" />
        <KpiTile label="Resale Sales" value="–" accent="#a855f7" />
        <KpiTile label="Resale Rental" value="–" accent="#ef4444" />
      </Box>

      <Divider sx={{ my: 2 }} />

      <SectionHeader icon={<GroupsIcon fontSize="small" />} title="Agents w/ Transaction"
        sub={`${district.code} · Huttons agents`} />
      {agentStatsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={20} /></Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <KpiTile icon={<ReceiptIcon />} label="Agents w/ Transaction"
              value={agentStats?.agentsWithTransaction ?? 0}
              sub={`in ${district.code}`} accent="#10b981" />
          </Box>
          {(agentStats?.byDivision ?? []).length > 0 && (
            <>
              <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', fontWeight: 600, display: 'block', mb: 0.5 }}>
                By division
              </Typography>
              <Box sx={{ mb: 1.5 }}>
                {(agentStats?.byDivision ?? []).map((d, i) => (
                  <ProgressRow
                    key={d.divisionName}
                    label={d.divisionName}
                    value={d.count}
                    max={divMax}
                    color={DIVISION_COLORS[i % DIVISION_COLORS.length]}
                    rightLabel={`${d.count} agents`}
                  />
                ))}
              </Box>
            </>
          )}
        </>
      )}
    </PanelShell>
  );
};

// ──────────────────────────────────────────────────────────────────────
// Heatmap helpers
// ──────────────────────────────────────────────────────────────────────
function heatColor(value: number, min: number, max: number): string {
  if (max <= min) return 'rgba(245,158,11,0.4)';
  const t = (value - min) / (max - min);
  const r = Math.round(34 + (239 - 34) * t);
  const g = Math.round(197 - (197 - 68) * t);
  const b = Math.round(94 - (94 - 68) * t);
  return `rgb(${r}, ${g}, ${b})`;
}

const ProjectMapLoadingOverlay: React.FC<{ tone: 'light' | 'dark' }> = ({ tone }) => {
  const foreground = tone === 'dark' ? '#f8fafc' : '#0f172a';
  return (
    <Box sx={{
      position: 'absolute', inset: 0, zIndex: 1200,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      pointerEvents: 'all', overflow: 'hidden',
      background: tone === 'dark' ? 'rgba(5,10,20,0.82)' : 'rgba(241,247,249,0.82)',
      backdropFilter: 'blur(5px)',
      '@keyframes situationRadarSweep': { to: { transform: 'rotate(360deg)' } },
      '@keyframes situationRadarPulse': {
        '0%': { transform: 'scale(0.25)', opacity: 0.9 },
        '75%, 100%': { transform: 'scale(1)', opacity: 0 },
      },
      '@keyframes situationRadarPing': {
        '0%, 100%': { transform: 'scale(0.8)', opacity: 0.4 },
        '50%': { transform: 'scale(1.35)', opacity: 1 },
      },
      '@keyframes situationRadarFloat': {
        '0%, 100%': { transform: 'translateY(0)' },
        '50%': { transform: 'translateY(-5px)' },
      },
      '@keyframes situationRadarShimmer': {
        '0%': { backgroundPosition: '200% 0' },
        '100%': { backgroundPosition: '-200% 0' },
      },
    }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', animation: 'situationRadarFloat 2.6s ease-in-out infinite' }}>
        <Box sx={{ position: 'relative', width: 150, height: 150, borderRadius: '50%', border: '1px solid rgba(16,185,129,0.42)', background: 'radial-gradient(circle, rgba(16,185,129,0.16) 0%, rgba(16,185,129,0.05) 45%, transparent 68%)', boxShadow: '0 0 42px rgba(16,185,129,0.22)' }}>
          {[26, 50, 74].map((size) => (
            <Box key={size} sx={{ position: 'absolute', width: `${size}%`, height: `${size}%`, left: `${(100 - size) / 2}%`, top: `${(100 - size) / 2}%`, borderRadius: '50%', border: '1px solid rgba(16,185,129,0.24)' }} />
          ))}
          <Box sx={{ position: 'absolute', left: '50%', top: 8, bottom: 8, borderLeft: '1px solid rgba(16,185,129,0.18)' }} />
          <Box sx={{ position: 'absolute', top: '50%', left: 8, right: 8, borderTop: '1px solid rgba(16,185,129,0.18)' }} />
          <Box sx={{ position: 'absolute', inset: 4, borderRadius: '50%', overflow: 'hidden', animation: 'situationRadarSweep 2.1s linear infinite' }}>
            <Box sx={{ position: 'absolute', left: '50%', top: '50%', width: '50%', height: '50%', transformOrigin: '0 0', background: 'conic-gradient(from 270deg at 0 0, rgba(16,185,129,0.82), rgba(16,185,129,0.2) 28deg, transparent 72deg)' }} />
          </Box>
          {[[33, 40], [67, 28], [58, 70], [28, 67]].map(([left, top], index) => (
            <Box key={`${left}-${top}`} sx={{ position: 'absolute', left: `${left}%`, top: `${top}%`, width: 7, height: 7, borderRadius: '50%', bgcolor: index === 0 ? '#f59e0b' : '#10b981', boxShadow: `0 0 12px ${index === 0 ? '#f59e0b' : '#10b981'}`, animation: `situationRadarPing 1.5s ease-in-out ${index * 220}ms infinite` }} />
          ))}
          <Box sx={{ position: 'absolute', inset: 35, borderRadius: '50%', border: '2px solid rgba(16,185,129,0.45)', animation: 'situationRadarPulse 2s ease-out infinite' }} />
        </Box>
        <Typography sx={{ mt: 2, fontSize: '0.84rem', fontWeight: 900, color: foreground, letterSpacing: 0.6 }}>SCANNING PROJECT LANDSCAPE</Typography>
        <Typography sx={{ mt: 0.4, fontSize: '0.68rem', color: tone === 'dark' ? 'rgba(226,232,240,0.68)' : 'rgba(51,65,85,0.72)' }}>Loading live project and transaction data…</Typography>
        <Box sx={{ width: 180, height: 3, mt: 1.4, borderRadius: 2, background: 'linear-gradient(90deg, transparent, #10b981, #f59e0b, transparent)', backgroundSize: '200% 100%', animation: 'situationRadarShimmer 1.7s linear infinite' }} />
      </Box>
    </Box>
  );
};

// ──────────────────────────────────────────────────────────────────────
// Main tab
// ──────────────────────────────────────────────────────────────────────
const SituationMapTab: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('cop');
  const [plotMode, setPlotMode] = useState<PlotMode>('pin');
  const [copMode, setCopMode] = useState<CopMode>('project');
  const [heatMetric, setHeatMetric] = useState<HeatMetric>('transactions');
  const [basemapId, setBasemapId] = useState<BasemapId>('voyager');
  const basemap = BASEMAPS[basemapId];

  const [selectedProject, setSelectedProject] = useState<MapProject | null>(null);
  const [selectedDistrict, setSelectedDistrict] = useState<DistrictInfo | null>(null);

  const [apiProjects, setApiProjects] = useState<MapProject[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [projectsLoadAttempt, setProjectsLoadAttempt] = useState(0);
  const [projectsWithTxIds, setProjectsWithTxIds] = useState<Set<string> | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [projectMarketShares, setProjectMarketShares] = useState<Record<string, { oursTx: number; eraTx: number; totalMarket: number }> | null>(null);
  const [shareRange, setShareRange] = useState<[number, number]>([0, 100]);
  const [winEraOnly, setWinEraOnly] = useState(false);
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setProjectsLoading(true);
    setProjectsError(null);
    setApiProjects([]);
    setProjectsWithTxIds(null);
    setProjectMarketShares(null);
    setSelectedProject(null);
    (async () => {
      try {
        const [config, txResult, sheetConfig, sharesResult] = await Promise.all([
          api.getProjectConfig(),
          api.getSituationMapProjectsWithTransactions().catch(() => ({ projectIds: [] as string[] })),
          api.getSheetConfig().catch((err) => {
            console.warn('SituationMap: failed to load sheet config for upcoming flag', err);
            return [] as Awaited<ReturnType<typeof api.getSheetConfig>>;
          }),
          api.getSituationMapMarketShares().catch(() => ({ shares: {} as Record<string, { oursTx: number; eraTx: number; totalMarket: number }> })),
        ]);
        if (!cancelled) setProjectMarketShares(sharesResult.shares);
        if (cancelled) return;

        const configIds = config.map((c) => c.projectId).filter(Boolean) as string[];
        const txIds: string[] = txResult.projectIds;
        setProjectsWithTxIds(new Set(txIds));

        // Project IDs from tx that are NOT in the regular config (e.g. SoldOut projects)
        const configIdSet = new Set(configIds);
        const supplementIds = txIds.filter((id) => !configIdSet.has(id));

        const [details, supplementResult] = await Promise.all([
          configIds.length > 0 ? api.getBulkProjectDetails(configIds) : Promise.resolve([]),
          supplementIds.length > 0
            ? api.getSituationMapSupplementDetails(supplementIds).catch(() => ({ projects: [] }))
            : Promise.resolve({ projects: [] }),
        ]);
        if (cancelled) return;

        const upcomingIds = new Set(
          sheetConfig.filter((s) => s.upcoming === true).map((s) => String(s.projectId))
        );
        const regularMapped = details
          .map((d) => mapDetailsToProject(d, upcomingIds))
          .filter((p): p is MapProject => p !== null);

        const supplementMapped = supplementResult.projects
          .map((d) => mapDetailsToProject(d, upcomingIds))
          .filter((p): p is MapProject => p !== null);

        setApiProjects([...regularMapped, ...supplementMapped]);
      } catch (err) {
        console.error('SituationMap: failed to load projects', err);
        if (!cancelled) setProjectsError(err instanceof Error ? err.message : 'Unable to load project data');
      } finally {
        if (!cancelled) setProjectsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectsLoadAttempt]);

  const activeProjects = useMemo(
    () => projectsWithTxIds !== null
      ? apiProjects.filter((p) =>
          projectsWithTxIds.has(p.id) ||        // has transactions since 1 Mar 2024
          p.soldUnits < p.totalUnits ||          // still has available units
          p.name.trim().toUpperCase().endsWith('GLS') // GLS project
        )
      : apiProjects,
    [apiProjects, projectsWithTxIds],
  );

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return activeProjects.filter((p) => p.name.toLowerCase().includes(q)).slice(0, 8);
  }, [activeProjects, searchQuery]);

  const visibleProjects = useMemo(() => {
    let projects = activeProjects;
    const q = searchQuery.trim().toLowerCase();
    if (q) projects = projects.filter((p) => p.name.toLowerCase().includes(q));
    if (projectMarketShares !== null) {
      const [minS, maxS] = shareRange;
      if (minS > 0 || maxS < 100) {
        projects = projects.filter((p) => {
          const s = projectMarketShares[p.id];
          const pct = s && s.totalMarket > 0 ? (s.oursTx / s.totalMarket) * 100 : 0;
          return pct >= minS && pct <= maxS;
        });
      }
      if (winEraOnly) {
        projects = projects.filter((p) => {
          const s = projectMarketShares[p.id];
          return s ? s.oursTx > s.eraTx : false;
        });
      }
    }
    return projects;
  }, [activeProjects, searchQuery, shareRange, winEraOnly, projectMarketShares]);

  // Real land-lot polygons from OneMap, keyed by project id
  const [landLots, setLandLots] = useState<Map<string, [number, number][][][]>>(new Map());
  const [landLotsLoading, setLandLotsLoading] = useState(false);

  useEffect(() => {
    if (apiProjects.length === 0) return;
    let cancelled = false;
    setLandLotsLoading(true);
    const points = apiProjects.map(p => ({ id: p.id, latitude: p.lat, longitude: p.lng }));
    const t0 = performance.now();
    fetchOnemapLandLotsBulk(points, 8)
      .then((results: OnemapBulkLandLotResultItem[]) => {
        if (cancelled) return;
        const map = new Map<string, [number, number][][][]>();
        for (const r of results) {
          const positions = geoJsonToLeafletPositions(r.geojson);
          if (positions) map.set(r.id, positions);
        }
        setLandLots(map);
        console.info(`[SituationMap] land lots: ${map.size}/${results.length} polygons in ${Math.round(performance.now() - t0)}ms`);
      })
      .catch((err) => console.error('SituationMap: failed to load land lots', err))
      .finally(() => { if (!cancelled) setLandLotsLoading(false); });
    return () => { cancelled = true; };
  }, [apiProjects]);

  const [labelPositions, setLabelPositions] = useState<Map<string, [number, number]>>(new Map());

  useEffect(() => {
    if (activeProjects.length === 0) return;
    setLabelPositions(computeSpreadLayout(activeProjects));
  }, [activeProjects]);

  const handleAutoArrange = () => {
    setLabelPositions(computeSpreadLayout(activeProjects));
  };

  const [kmlPolygons, setKmlPolygons] = useState<KmlPolygon[]>([]);
  useEffect(() => {
    let cancelled = false;
    loadKml(districtKmlUrl).then((polys) => {
      if (!cancelled) setKmlPolygons(polys);
    }).catch((err) => {
      console.error('Failed to load district KML', err);
    });
    return () => { cancelled = true; };
  }, []);

  // Build per-district sold-unit totals from real project data
  const districtSoldUnits = useMemo(() => {
    const m = new Map<string, number>();
    activeProjects.forEach(p => {
      if (!p.district) return;
      const code = p.district.startsWith('D') ? p.district : `D${p.district}`;
      m.set(code, (m.get(code) ?? 0) + p.soldUnits);
    });
    return m;
  }, [activeProjects]);

  // DISTRICTS with transactions overridden by real sold-unit data
  const activeDistricts = useMemo(() =>
    DISTRICTS.map(d => ({
      ...d,
      metrics: {
        ...d.metrics,
        transactions: districtSoldUnits.has(d.code) ? (districtSoldUnits.get(d.code) ?? 0) : d.metrics.transactions,
      },
    })),
  [districtSoldUnits]);

  const sgTransactionsTotal = useMemo(
    () => activeDistricts.reduce((s, d) => s + d.metrics.transactions, 0),
    [activeDistricts],
  );

  const districtByCode = useMemo(() => {
    const m = new Map<string, DistrictInfo>();
    activeDistricts.forEach((d) => m.set(d.code, d));
    return m;
  }, [activeDistricts]);

  const districtMetricValues = useMemo(() => activeDistricts.map((d) => d.metrics[heatMetric]), [activeDistricts, heatMetric]);
  const minMetric = Math.min(...districtMetricValues);
  const maxMetric = Math.max(...districtMetricValues);

  const showRadius = viewMode === 'cop' && copMode === 'project' && selectedProject;

  // Top of page summary KPIs (always visible)
  const totalProjects = activeProjects.filter(p => p.type === 'new-launch').length;
  const totalGls = activeProjects.filter(p => p.type === 'gls').length;
  const totalUnits = activeProjects.reduce((s, p) => s + p.totalUnits, 0);
  const totalSold = activeProjects.reduce((s, p) => s + p.soldUnits, 0);
  const soldPercent = totalUnits > 0 ? Math.round((totalSold / totalUnits) * 100) : 0;
  const totalDistricts = activeDistricts.length;

  const renderProjectMarkers = (onClick: (p: MapProject) => void) => {
    if (plotMode === 'plot') {
      return visibleProjects.map((p) => {
        const isSelected = selectedProject?.id === p.id;
        const color = isSelected ? SELECTED_COLOR : p.type === 'gls' ? GLS_COLOR : PROJECT_COLOR;
        const realPositions = landLots.get(p.id);
        const positions = realPositions ?? p.plot;
        return (
          <Polygon
            key={p.id}
            positions={positions as any}
            pathOptions={{ color, fillColor: color, fillOpacity: 0.55, weight: 2 }}
            eventHandlers={{ click: () => onClick(p) }}
          >
            <Popup>
              <strong>{p.name}</strong><br />
              {p.developer}<br />
              {p.district} · {p.region}{p.topYear ? ` · TOP ${p.topYear}` : ''}
            </Popup>
          </Polygon>
        );
      });
    }
    if (plotMode === 'label') {
      return visibleProjects.filter((p) => p.upcoming === true || /\bGLS\s*$/i.test(p.name)).flatMap((p) => {
        const isSelected = selectedProject?.id === p.id;
        const color = p.type === 'gls' ? GLS_COLOR : PROJECT_COLOR;
        const labelPos: [number, number] = labelPositions.get(p.id) ?? [p.lat, p.lng];
        const hasMoved = labelPos[0] !== p.lat || labelPos[1] !== p.lng;
        return [
          <CircleMarker
            key={`dot-${p.id}`}
            center={[p.lat, p.lng]}
            radius={isSelected ? 6 : 4}
            pathOptions={{ color: '#fff', fillColor: isSelected ? SELECTED_COLOR : color, fillOpacity: 1, weight: 2 }}
            eventHandlers={{ click: () => onClick(p) }}
          />,
          hasMoved && (
            <Polyline
              key={`line-${p.id}`}
              positions={[[p.lat, p.lng], labelPos]}
              pathOptions={{ color: isSelected ? SELECTED_COLOR : color, weight: 1.2, opacity: 0.55, dashArray: '5 4' }}
            />
          ),
          <Marker
            key={`label-${p.id}`}
            position={labelPos}
            icon={labelIcon(p, isSelected, basemap.tone)}
            zIndexOffset={isSelected ? 1000 : 0}
            draggable={true}
            eventHandlers={{
              click: () => onClick(p),
              dragend: (e: any) => {
                const { lat, lng } = e.target.getLatLng();
                setLabelPositions(prev => {
                  const next = new Map(prev);
                  next.set(p.id, [lat, lng]);
                  return next;
                });
              },
            }}
          />,
        ].filter(Boolean) as React.ReactElement[];
      });
    }
    return visibleProjects.map((p) => {
      const isSelected = selectedProject?.id === p.id;
      const color = isSelected ? SELECTED_COLOR : p.type === 'gls' ? GLS_COLOR : PROJECT_COLOR;
      return (
        <Marker
          key={p.id}
          position={[p.lat, p.lng]}
          icon={pinIcon(color, isSelected ? 34 : 26)}
          eventHandlers={{ click: () => onClick(p) }}
        >
          <Popup>
            <strong>{p.name}</strong><br />
            {p.developer}<br />
            {p.district} · {p.region}{p.topYear ? ` · TOP ${p.topYear}` : ''}
          </Popup>
        </Marker>
      );
    });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header row: title + summary KPIs */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'stretch' }}>
        <Card sx={{ flex: '1 1 240px', minWidth: 240 }}>
          <CardContent sx={{ py: 1.5 }}>
            <Typography variant="overline" sx={{ color: 'var(--color-accent)', fontWeight: 700 }}>
              Situation Map
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--color-text-strong)', lineHeight: 1.1 }}>
              Singapore — Live Ground View
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)' }}>
              {projectsLoading ? 'Loading projects…' : `${totalProjects} new launches · ${totalGls} GLS · ${totalDistricts} districts`}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 160px', minWidth: 160 }}>
          <CardContent sx={{ py: 1.5 }}>
            <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', fontWeight: 600 }}>Total Units</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'var(--color-text-strong)' }}>{totalUnits.toLocaleString()}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 160px', minWidth: 160 }}>
          <CardContent sx={{ py: 1.5 }}>
            <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', fontWeight: 600 }}>Sold</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#10b981' }}>
              {totalSold.toLocaleString()}
              <Typography component="span" variant="body2" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', ml: 0.75 }}>
                ({soldPercent}%)
              </Typography>
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 160px', minWidth: 160 }}>
          <CardContent sx={{ py: 1.5 }}>
            <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.7)', fontWeight: 600 }}>Available</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#f59e0b' }}>
              {(totalUnits - totalSold).toLocaleString()}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Controls */}
      <Card sx={{
        background: 'linear-gradient(180deg, rgba(var(--color-text-rgb), 0.02), rgba(var(--color-text-rgb), 0))',
        border: '1px solid rgba(var(--color-text-rgb), 0.08)',
      }}>
        <CardContent sx={{ py: 1.75, '&:last-child': { pb: 1.75 } }}>
          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: { xs: 2, md: 3 },
            alignItems: 'flex-end',
            rowGap: 2,
          }}>
            <ControlGroup label="View">
              <Segmented<ViewMode>
                value={viewMode}
                onChange={(v) => {
                  setViewMode(v);
                  setSelectedProject(null);
                  setSelectedDistrict(null);
                }}
                options={[
                  { value: 'project', label: 'Project View', icon: <LayersIcon /> },
                  { value: 'cop', label: 'Situation Picture', icon: <CenterFocusStrongIcon /> },
                ]}
              />
            </ControlGroup>

            {viewMode === 'cop' && (
              <ControlGroup label="Focus">
                <Segmented<CopMode>
                  value={copMode}
                  onChange={(v) => {
                    setCopMode(v);
                    setSelectedProject(null);
                    setSelectedDistrict(null);
                  }}
                  options={[
                    { value: 'project', label: 'Project', icon: <ApartmentIcon /> },
                    { value: 'district', label: 'District', icon: <GridViewIcon /> },
                  ]}
                />
              </ControlGroup>
            )}

            {((viewMode === 'project') || (viewMode === 'cop' && copMode === 'project')) && (
              <ControlGroup label="Render">
                <Segmented<PlotMode>
                  value={plotMode}
                  onChange={setPlotMode}
                  options={[
                    { value: 'pin', label: 'Pin', icon: <PlaceIcon /> },
                    { value: 'plot', label: 'Plot', icon: <ApartmentIcon /> },
                    { value: 'label', label: 'Label (Upcoming & GLS)', icon: <MapIcon /> },
                  ]}
                />
              </ControlGroup>
            )}

            {viewMode === 'cop' && copMode === 'district' && (
              <ControlGroup label="Heatmap Metric">
                <Segmented<HeatMetric>
                  value={heatMetric}
                  onChange={setHeatMetric}
                  options={[
                    { value: 'transactions', label: 'Transactions' },
                    { value: 'listings', label: 'Listings' },
                    { value: 'eoi', label: 'EOI' },
                  ]}
                />
              </ControlGroup>
            )}

            <ControlGroup label="Basemap">
              <Segmented<BasemapId>
                value={basemapId}
                onChange={setBasemapId}
                size="sm"
                options={[
                  { value: 'voyager', label: 'Voyager' },
                  { value: 'light', label: 'Light' },
                  { value: 'dark', label: 'Dark' },
                  { value: 'streets', label: 'Streets' },
                  { value: 'satellite', label: 'Satellite' },
                ]}
              />
            </ControlGroup>

            <Box sx={{ flex: 1, minWidth: 0 }} />

            {plotMode === 'label' && (
              <ControlGroup label="Layout">
                <ButtonBase
                  onClick={handleAutoArrange}
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.6,
                    px: 1.5, py: 0.75, borderRadius: 999, fontSize: '0.78rem', fontWeight: 600,
                    bgcolor: 'rgba(var(--color-accent-rgb), 0.12)', color: 'var(--color-accent)',
                    border: '1px solid rgba(var(--color-accent-rgb), 0.25)',
                    '&:hover': { bgcolor: 'rgba(var(--color-accent-rgb), 0.2)' },
                    '& svg': { fontSize: 15 },
                  }}
                >
                  <AutoFixHighIcon />
                  Auto Arrange
                </ButtonBase>
              </ControlGroup>
            )}

            <ControlGroup label="Legend">
              <Box sx={{
                display: 'inline-flex',
                gap: 0,
                bgcolor: 'rgba(var(--color-text-rgb), 0.06)',
                border: '1px solid rgba(var(--color-text-rgb), 0.08)',
                borderRadius: 999,
                px: 1.25,
                py: 0.75,
                alignItems: 'center',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, pr: 1.25, borderRight: '1px solid rgba(var(--color-text-rgb), 0.12)' }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: PROJECT_COLOR, boxShadow: `0 0 0 2px rgba(245,158,11,0.18)` }} />
                  <Typography variant="caption" sx={{ color: 'var(--color-text-strong)', fontWeight: 600 }}>New Launch</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, pl: 1.25 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: GLS_COLOR, boxShadow: `0 0 0 2px rgba(139,92,246,0.18)` }} />
                  <Typography variant="caption" sx={{ color: 'var(--color-text-strong)', fontWeight: 600 }}>GLS</Typography>
                </Box>
              </Box>
            </ControlGroup>
          </Box>

          <Box sx={{
            mt: 1.5,
            pt: 1.25,
            borderTop: '1px dashed rgba(var(--color-text-rgb), 0.1)',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}>
            {viewMode === 'cop' && copMode === 'district' && (
              <WhatshotIcon sx={{ fontSize: 14, color: 'var(--color-accent)' }} />
            )}
            {(viewMode === 'project' || (viewMode === 'cop' && copMode === 'project')) && (
              <PlaceIcon sx={{ fontSize: 14, color: 'var(--color-accent)' }} />
            )}
            <Typography variant="caption" sx={{ color: 'rgba(var(--color-text-rgb), 0.75)' }}>
              {viewMode === 'project' && (plotMode === 'plot' && landLotsLoading ? 'Fetching real land-lot polygons from OneMap…' : 'Tap a pin or plot to see project details.')}
              {viewMode === 'cop' && copMode === 'project' && 'Tap a project to view market share vs competitors, agent profile and Super Picker adoption within 5km.'}
              {viewMode === 'cop' && copMode === 'district' && 'Districts are shaded by the selected metric. Tap a district for transactions / listings share of SG and agent analytics.'}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Map */}
      <Card sx={{
        position: 'relative',
        overflow: 'hidden',
        p: 0,
        border: '1px solid rgba(var(--color-text-rgb), 0.08)',
        '& .leaflet-container': {
          fontFamily: 'inherit',
          background: basemap.background,
        },
        '& .leaflet-control-zoom': {
          border: 'none',
          boxShadow: basemap.tone === 'dark' ? '0 4px 14px rgba(0,0,0,0.55)' : '0 4px 14px rgba(15,23,42,0.18)',
          borderRadius: 10,
          overflow: 'hidden',
        },
        '& .leaflet-control-zoom a': {
          background: basemap.tone === 'dark' ? 'rgba(20,28,40,0.92)' : '#ffffff',
          color: basemap.tone === 'dark' ? '#e2e8f0' : '#1f2937',
          border: 'none',
          borderBottom: basemap.tone === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,23,42,0.08)',
          width: 32,
          height: 32,
          lineHeight: '32px',
          fontSize: 18,
          fontWeight: 600,
        },
        '& .leaflet-control-zoom a:last-child': { borderBottom: 'none' },
        '& .leaflet-control-zoom a:hover': {
          background: basemap.tone === 'dark' ? 'rgba(40,52,72,0.95)' : '#f3f4f6',
        },
        '& .leaflet-control-attribution': {
          background: basemap.tone === 'dark' ? 'rgba(20,28,40,0.7)' : 'rgba(255,255,255,0.82)',
          color: basemap.tone === 'dark' ? 'rgba(226,232,240,0.7)' : '#475569',
          fontSize: '10px',
          padding: '2px 6px',
          borderRadius: 4,
          backdropFilter: 'blur(4px)',
        },
        '& .leaflet-control-attribution a': { color: basemap.tone === 'dark' ? 'rgba(226,232,240,0.9)' : '#1f2937' },
        '& .leaflet-popup-content-wrapper': {
          background: basemap.tone === 'dark' ? '#1a2436' : '#ffffff',
          color: basemap.tone === 'dark' ? '#e2e8f0' : '#0f172a',
          borderRadius: 10,
          border: basemap.tone === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(15,23,42,0.08)',
          boxShadow: basemap.tone === 'dark' ? '0 8px 24px rgba(0,0,0,0.55)' : '0 8px 24px rgba(15,23,42,0.18)',
        },
        '& .leaflet-popup-content': { margin: '10px 14px', lineHeight: 1.45, fontSize: '0.8rem' },
        '& .leaflet-popup-content strong': { color: basemap.tone === 'dark' ? '#fff' : '#0f172a' },
        '& .leaflet-popup-tip': { background: basemap.tone === 'dark' ? '#1a2436' : '#ffffff' },
        '& .leaflet-popup-close-button': { color: basemap.tone === 'dark' ? 'rgba(226,232,240,0.7) !important' : '#64748b !important' },
      }}>
        <Box sx={{ position: 'relative', height: { xs: 520, md: 'calc(100vh - 360px)' }, minHeight: 480 }}>
          {/* ── Floating filter panel ── */}
          {(() => {
            const filterActive = shareRange[0] > 0 || shareRange[1] < 100 || winEraOnly;
            const bg = basemap.tone === 'dark' ? 'rgba(15,23,42,0.95)' : 'rgba(255,255,255,0.97)';
            const border = (active: boolean) =>
              `1.5px solid ${active ? '#f59e0b' : 'rgba(0,0,0,0.14)'}`;
            return (
              <Box sx={{ position: 'absolute', top: 12, left: 12, zIndex: 1000, pointerEvents: 'none' }}>
                <Box sx={{ pointerEvents: 'auto' }}>
                  {/* Toggle button */}
                  <ButtonBase
                    onClick={() => setFilterPanelOpen((v) => !v)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 0.75,
                      px: 1.4, py: 0.8,
                      borderRadius: 2.5,
                      background: bg,
                      border: border(filterActive || filterPanelOpen),
                      boxShadow: '0 4px 20px rgba(0,0,0,0.22)',
                      backdropFilter: 'blur(10px)',
                      transition: 'border-color 150ms',
                    }}
                  >
                    <TuneIcon sx={{ fontSize: 16, color: filterActive ? '#f59e0b' : 'rgba(100,116,139,0.8)' }} />
                    <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: basemap.tone === 'dark' ? '#e2e8f0' : '#1f2937' }}>
                      Filters
                    </Typography>
                    {filterActive && (
                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#f59e0b', ml: 0.25 }} />
                    )}
                  </ButtonBase>

                  {/* Expanded panel */}
                  {filterPanelOpen && (
                    <Box sx={{
                      mt: 0.75, p: 2,
                      borderRadius: 2,
                      background: bg,
                      border: '1px solid rgba(0,0,0,0.1)',
                      boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
                      backdropFilter: 'blur(10px)',
                      width: 270,
                    }}>
                      {/* Huttons Market Share slider */}
                      <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: 'rgba(100,116,139,0.85)', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
                        Huttons Market Share
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#f59e0b' }}>{shareRange[0]}%</Typography>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#f59e0b' }}>{shareRange[1]}%</Typography>
                      </Box>
                      <Slider
                        value={shareRange}
                        onChange={(_, v) => setShareRange(v as [number, number])}
                        min={0} max={100} step={1}
                        disableSwap
                        sx={{
                          color: '#f59e0b',
                          '& .MuiSlider-thumb': { width: 14, height: 14, boxShadow: '0 2px 6px rgba(245,158,11,0.5)' },
                          '& .MuiSlider-rail': { opacity: 0.25 },
                          py: 1,
                        }}
                      />
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: -0.5, mb: 1.5 }}>
                        <Typography sx={{ fontSize: '0.62rem', color: 'rgba(100,116,139,0.6)' }}>0%</Typography>
                        <Typography sx={{ fontSize: '0.62rem', color: 'rgba(100,116,139,0.6)' }}>100%</Typography>
                      </Box>

                      {/* Win ERA button */}
                      <ButtonBase
                        onClick={() => setWinEraOnly((v) => !v)}
                        sx={{
                          width: '100%', py: 0.9, px: 1.25, borderRadius: 1.5,
                          display: 'flex', alignItems: 'center', gap: 0.75,
                          bgcolor: winEraOnly ? 'rgba(245,158,11,0.15)' : 'rgba(100,116,139,0.08)',
                          border: `1.5px solid ${winEraOnly ? '#f59e0b' : 'rgba(100,116,139,0.2)'}`,
                          transition: 'all 150ms',
                          '&:hover': { bgcolor: winEraOnly ? 'rgba(245,158,11,0.22)' : 'rgba(100,116,139,0.14)' },
                        }}
                      >
                        <EmojiEventsIcon sx={{ fontSize: 16, color: winEraOnly ? '#f59e0b' : 'rgba(100,116,139,0.6)' }} />
                        <Box sx={{ flex: 1, textAlign: 'left' }}>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: winEraOnly ? '#b45309' : (basemap.tone === 'dark' ? '#e2e8f0' : '#374151'), lineHeight: 1.2 }}>
                            Win ERA Only
                          </Typography>
                          <Typography sx={{ fontSize: '0.62rem', color: 'rgba(100,116,139,0.7)', lineHeight: 1.2 }}>
                            Huttons tx &gt; ERA tx
                          </Typography>
                        </Box>
                        <Box sx={{
                          width: 18, height: 18, borderRadius: '50%',
                          bgcolor: winEraOnly ? '#f59e0b' : 'rgba(100,116,139,0.15)',
                          border: `2px solid ${winEraOnly ? '#d97706' : 'rgba(100,116,139,0.3)'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0,
                        }}>
                          {winEraOnly && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#fff' }} />}
                        </Box>
                      </ButtonBase>

                      {/* Reset */}
                      {filterActive && (
                        <ButtonBase
                          onClick={() => { setShareRange([0, 100]); setWinEraOnly(false); }}
                          sx={{ mt: 1.25, width: '100%', py: 0.6, borderRadius: 1.5, fontSize: '0.72rem', fontWeight: 600, color: 'rgba(100,116,139,0.7)', bgcolor: 'rgba(100,116,139,0.07)', '&:hover': { bgcolor: 'rgba(100,116,139,0.13)' } }}
                        >
                          Reset filters
                        </ButtonBase>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            );
          })()}

          {/* ── Floating search bar ── */}
          <Box sx={{ position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 1000, width: 300, pointerEvents: 'none' }}>
            <Box sx={{ pointerEvents: 'auto' }}>
              <Box sx={
                {
                  display: 'flex', alignItems: 'center', gap: 0.75,
                  px: 1.5, py: 0.875,
                  borderRadius: 2.5,
                  background: basemap.tone === 'dark' ? 'rgba(15,23,42,0.95)' : 'rgba(255,255,255,0.97)',
                  border: `1.5px solid ${searchFocused ? '#f59e0b' : 'rgba(0,0,0,0.14)'}`,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.22)',
                  backdropFilter: 'blur(10px)',
                  transition: 'border-color 150ms ease',
                }
              }>
                <SearchIcon sx={{ fontSize: 18, color: searchFocused ? '#f59e0b' : 'rgba(100,116,139,0.7)', flexShrink: 0, transition: 'color 150ms' }} />
                <InputBase
                  placeholder="Search project…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                  sx={
                    {
                      flex: 1,
                      fontSize: '0.82rem',
                      fontWeight: 500,
                      color: basemap.tone === 'dark' ? '#e2e8f0' : '#0f172a',
                      '& input::placeholder': { color: 'rgba(100,116,139,0.7)', opacity: 1 },
                    }
                  }
                />
                {searchQuery && (
                  <IconButton size="small" onClick={() => { setSearchQuery(''); }} sx={{ p: 0.25, color: 'rgba(100,116,139,0.7)', '& svg': { fontSize: 16 } }}>
                    <ClearIcon />
                  </IconButton>
                )}
              </Box>
              {searchFocused && searchResults.length > 0 && (
                <Box sx={
                  {
                    mt: 0.75,
                    borderRadius: 2,
                    background: basemap.tone === 'dark' ? 'rgba(15,23,42,0.97)' : 'rgba(255,255,255,0.99)',
                    border: '1px solid rgba(0,0,0,0.1)',
                    boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
                    backdropFilter: 'blur(10px)',
                    overflow: 'hidden',
                  }
                }>
                  {searchResults.map((p, i) => (
                    <ButtonBase
                      key={p.id}
                      onMouseDown={() => {
                        setSelectedProject(p);
                        setSelectedDistrict(null);
                        setSearchQuery(p.name);
                        setSearchFocused(false);
                      }}
                      sx={
                        {
                          display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                          width: '100%', px: 1.5, py: 0.875, textAlign: 'left',
                          borderBottom: i < searchResults.length - 1 ? '1px solid rgba(0,0,0,0.06)' : 'none',
                          '&:hover': { bgcolor: 'rgba(245,158,11,0.07)' },
                        }
                      }
                    >
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: basemap.tone === 'dark' ? '#e2e8f0' : '#0f172a', lineHeight: 1.3 }}>
                        {p.name}
                      </Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: 'rgba(100,116,139,0.85)', mt: 0.1 }}>
                        {[p.district, p.region, p.topYear ? `TOP ${p.topYear}` : null].filter(Boolean).join(' · ')}
                      </Typography>
                    </ButtonBase>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
          {projectsLoading && <ProjectMapLoadingOverlay tone={basemap.tone} />}
          <MapContainer
            bounds={SG_BOUNDS}
            maxBounds={SG_MAX_BOUNDS}
            maxBoundsViscosity={1.0}
            minZoom={11}
            maxZoom={18}
            style={{ height: '100%', width: '100%', background: basemap.background }}
            scrollWheelZoom
            zoomControl={false}
            attributionControl={false}
          >
            <FitSingapore />
            <TileLayer
              key={basemap.id}
              attribution={basemap.attribution}
              url={basemap.url}
              {...(basemap.subdomains ? { subdomains: basemap.subdomains } : {})}
            />
            <ZoomControlBottomRight />
            <AttributionInline attribution={basemap.attribution} />
            <FlyToProject project={selectedProject} />

            {viewMode === 'project' && renderProjectMarkers((p) => {
              setSelectedProject(p);
              setSelectedDistrict(null);
            })}

            {viewMode === 'cop' && copMode === 'project' && renderProjectMarkers((p) => {
              setSelectedProject(p);
              setSelectedDistrict(null);
            })}

            {showRadius && selectedProject && (
              <Circle
                center={[selectedProject.lat, selectedProject.lng]}
                radius={5000}
                pathOptions={{
                  color: SELECTED_COLOR,
                  fillColor: SELECTED_COLOR,
                  fillOpacity: 0.08,
                  weight: 2,
                  dashArray: '6 4',
                }}
              />
            )}

            {viewMode === 'cop' && copMode === 'district' && kmlPolygons.length > 0 && kmlPolygons.map((poly) => {
              const d = districtByCode.get(poly.districtCode);
              if (!d) return null;
              const v = d.metrics[heatMetric];
              const color = heatColor(v, minMetric, maxMetric);
              const isSelected = selectedDistrict?.code === d.code;
              const strokeColor = isSelected
                ? (basemap.tone === 'dark' ? '#fff' : '#0f172a')
                : (basemap.tone === 'dark' ? 'rgba(255,255,255,0.45)' : color);
              return (
                <Polygon
                  key={poly.id}
                  positions={poly.coordinates}
                  pathOptions={{
                    color: strokeColor,
                    weight: isSelected ? 2.5 : (basemap.tone === 'dark' ? 1.2 : 1),
                    fillColor: color,
                    fillOpacity: isSelected ? 0.7 : (basemap.id === 'satellite' ? 0.6 : 0.55),
                  }}
                  eventHandlers={{
                    click: () => {
                      setSelectedDistrict(d);
                      setSelectedProject(null);
                    },
                  }}
                >
                  <Popup>
                    <strong>{d.code} · {d.name}</strong><br />
                    {heatMetric}: {v}
                  </Popup>
                </Polygon>
              );
            })}

            {/* Fallback to rectangle districts if KML hasn't loaded yet */}
            {viewMode === 'cop' && copMode === 'district' && kmlPolygons.length === 0 && DISTRICTS.map((d) => {
              const v = d.metrics[heatMetric];
              const color = heatColor(v, minMetric, maxMetric);
              const isSelected = selectedDistrict?.code === d.code;
              return (
                <Polygon
                  key={d.code}
                  positions={d.polygon}
                  pathOptions={{
                    color: isSelected ? '#0f172a' : color,
                    weight: isSelected ? 2.5 : 1,
                    fillColor: color,
                    fillOpacity: 0.4,
                    dashArray: '4 4',
                  }}
                  eventHandlers={{
                    click: () => {
                      setSelectedDistrict(d);
                      setSelectedProject(null);
                    },
                  }}
                >
                  <Popup>
                    <strong>{d.code} · {d.name}</strong><br />
                    {heatMetric}: {v}
                  </Popup>
                </Polygon>
              );
            })}
          </MapContainer>

          {!projectsLoading && projectsError && (
            <Box sx={{ position: 'absolute', inset: 0, zIndex: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <Box sx={{ pointerEvents: 'auto', maxWidth: 360, mx: 2, p: 2.25, textAlign: 'center', borderRadius: 2.5, bgcolor: 'var(--color-surface)', border: '1px solid rgba(239,68,68,0.3)', boxShadow: '0 12px 32px rgba(0,0,0,0.28)' }}>
                <Typography sx={{ fontSize: '0.88rem', fontWeight: 800, color: 'var(--color-text-strong)' }}>Live project data is unavailable</Typography>
                <Typography sx={{ mt: 0.5, fontSize: '0.68rem', color: 'rgba(var(--color-text-rgb), 0.65)' }}>{projectsError}</Typography>
                <ButtonBase onClick={() => setProjectsLoadAttempt((attempt) => attempt + 1)} sx={{ mt: 1.35, px: 1.5, py: 0.7, borderRadius: 999, bgcolor: '#f59e0b', color: '#fff', fontSize: '0.7rem', fontWeight: 800, '&:hover': { bgcolor: '#d97706' } }}>Retry</ButtonBase>
              </Box>
            </Box>
          )}

          {!projectsLoading && !projectsError && activeProjects.length === 0 && (
            <Box sx={{ position: 'absolute', inset: 0, zIndex: 850, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <Box sx={{ px: 2, py: 1.25, borderRadius: 2, bgcolor: 'var(--color-surface)', border: '1px solid rgba(var(--color-text-rgb), 0.1)', boxShadow: '0 8px 24px rgba(0,0,0,0.2)' }}>
                <Typography sx={{ fontSize: '0.76rem', fontWeight: 700, color: 'rgba(var(--color-text-rgb), 0.72)' }}>No mapped projects were returned.</Typography>
              </Box>
            </Box>
          )}

          {viewMode === 'project' && selectedProject && (
            <ProjectInfoPanel project={selectedProject} onClose={() => setSelectedProject(null)} />
          )}
          {viewMode === 'cop' && copMode === 'project' && selectedProject && (
            <ProjectCopPanel project={selectedProject} onClose={() => setSelectedProject(null)} />
          )}
          {viewMode === 'cop' && copMode === 'district' && selectedDistrict && (
            <DistrictCopPanel district={selectedDistrict} metric={heatMetric} sgTransactionsTotal={sgTransactionsTotal} onClose={() => setSelectedDistrict(null)} />
          )}

          {viewMode === 'cop' && copMode === 'district' && (
            <Box sx={{
              position: 'absolute',
              left: 12, bottom: 12,
              zIndex: 500,
              bgcolor: 'var(--color-surface)',
              border: '1px solid rgba(var(--color-text-rgb), 0.1)',
              borderRadius: 1.5,
              p: 1.25,
              boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
              minWidth: 220,
            }}>
              <Typography variant="caption" sx={{ fontWeight: 700, color: 'var(--color-text-strong)', display: 'block', mb: 0.5 }}>
                <TerrainIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'text-bottom' }} />
                Heatmap · {heatMetric}
              </Typography>
              <Box sx={{
                height: 8, borderRadius: 4,
                background: 'linear-gradient(to right, rgb(34,197,94), rgb(234,179,8), rgb(239,68,68))',
                mb: 0.5,
              }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption">{minMetric}</Typography>
                <Typography variant="caption">{maxMetric}</Typography>
              </Box>
            </Box>
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default SituationMapTab;
